import * as THREE from 'three'
import type { GSplatClipState } from '@/plugins/gsplat/clipping/GSplatClipState'
import {
  GSplatUnifiedSorter,
  type OnSortedCallback,
  type SortRequestResult,
  type SortResultMetadata,
} from '@/plugins/gsplat/sorting/GSplatUnifiedSorter'
import {
  GSplatPicker,
  type GSplatPickOptions,
  type GSplatPickResult,
} from '@/plugins/gsplat/spatial/GSplatPicker'
import { hasGeometry, hasRefCounting } from '@/plugins/gsplat/types/ManagerTypes'
import type { IGSplatGeometry } from '@/plugins/gsplat/types/ResourceTypes'
import { ClipTask, DEFAULT_MAX_PICK_RESULTS } from '@/shared/constants'
import { getLoggerManager } from '@/shared/logging'
import {
  type CompiledGSplatWorkBufferModifier,
  GSPLAT_WORKBUFFER_UPDATE_ALWAYS,
  GSPLAT_WORKBUFFER_UPDATE_AUTO,
  GSPLAT_WORKBUFFER_UPDATE_ONCE,
  type GSplatClipLodDiagnostics,
  type GSplatFirstOrderUploadMode,
  type GSplatInteractiveSortSchedulerConfig,
  type GSplatOrderUploadMode,
  type GSplatVisualActivitySnapshot,
  type GSplatWorkBufferUpdateMode,
  type ScalarHistogramResult,
} from '@/shared/types/gsplat'
import { GSplatBudgetBalancer } from './GSplatBudgetBalancer'
import { GSplatInfo } from './GSplatInfo'
import type { GSplatOctree } from './GSplatOctree'
import { GSplatOctreeInstance } from './GSplatOctreeInstance'
import { GSplatParams } from './GSplatParams'
import type { GSplatPlacement } from './GSplatPlacement'
import {
  GSplatScalarMode,
  GSplatWorkBuffer,
  type GSplatWorkBufferAdvanceOrderStagingResult,
  type GSplatWorkBufferOrderUploadResult,
} from './GSplatWorkBuffer'
import { GSplatWorldState } from './GSplatWorldState'
import { clampPercentile, computeScalarHistogram } from './scalarHistogram'

const log = getLoggerManager().getLogger('gsplat:lod')

/**
 * 排序参数接口
 */
interface SortParameters {
  command: 'intervals'
  textureSize: number
  totalActiveSplats: number
  version: number
  ids: number[]
  pixelOffsets: number[][]
  intervals: number[][]
}

export interface GSplatOrderUploadSchedulerOptions {
  orderUploadMode?: GSplatOrderUploadMode
  minOrderUploadIntervalMs?: number
  maxOrderUploadDelayMs?: number
  maxImmediateOrderUploadBytes?: number
  maxOrderUploadBytesPerFrame?: number
  maxOrderUploadChunksPerFrame?: number
  firstOrderUploadMode?: GSplatFirstOrderUploadMode
  allowFirstOrderFallback?: boolean
  allowFullUploadFallback?: boolean
}

export interface GSplatManagerOptions {
  orderUploadScheduler?: GSplatOrderUploadSchedulerOptions
  interactiveSortScheduler?: GSplatInteractiveSortSchedulerConfig
  nowMs?: () => number
  onSortResultReady?: () => void
}

export interface GSplatBudgetRuntimeDiagnostics {
  budgetScale: number
  globalMaxDistance: number
  adjustedBudget: number
  paddingEstimate: number
  rawOptimalSplats: number
  budgetTextureWidth: number
  fixedSplats: number
  octreeBudget: number
  cameraAspect: number
}

export interface GSplatOrderUploadDiagnostics {
  scheduledUploadCount: number
  scheduledUploadBytes: number
  lastScheduledUploadBytes: number
  lastScheduledTextureSize: number
  lastScheduledAtMs: number | null
  lastApplyReason: string | null
  lastAppliedSortGeneration: number
  pendingAgeMs: number | null
  pendingSortGeneration: number | null
  pendingWorldStateVersion: number | null
  pendingOrderSkippedFrameCount: number | null
  lastAppliedPendingOrderSkippedFrameCount: number
  coalescedResultCount: number
  staleResultCount: number
  observedUploadCount: number
  lastObservedAtMs: number | null
  observedUploadAvailable: boolean
  submittedRequestCount: number
  queuedRequestCount: number
  replayedRequestCount: number
  overwrittenRequestCount: number
  rejectedRequestCount: number
  workerErrorCount: number
  pendingRequestAgeMs: number | null
  lastRequestToSubmitLatencyMs: number | null
  pendingRequestToSubmitLatencyMs: number | null
  lastWorkerToApplyLatencyMs: number | null
  pendingWorkerToApplyLatencyMs: number | null
  orderBufferConservativeCopyCount: number
  orderBufferSynchronousCopyCount: number
  orderBufferAllocCount: number
  orderBufferReuseCount: number
  orderBufferReleaseCount: number
  pendingOrderBufferLeaseCount: number
  adaptiveReplayDeferralCount: number
  adaptiveReplayTimerFireCount: number
  adaptiveSortIntervalMs: number
  workerSortTimeEmaMs: number
  pendingReplayDelayMs: number | null
  stagingSortGeneration: number | null
  stagingTextureSize: number | null
  stagingVisibleCount: number | null
  stagingTotalBytes: number
  stagingSubmittedBytes: number
  stagingRemainingBytes: number
  stagingSubmittedChunks: number
  stagingRemainingChunks: number
  lastFrameSubmittedBytes: number
  lastFrameSubmittedChunks: number
  byteBudgetBlockedCount: number
  chunkBudgetBlockedCount: number
  minRowBudgetOverrideCount: number
  chunkedUploadCount: number
  fullUploadCount: number
  fallbackUploadCount: number
  abortedStagingJobCount: number
  activeCommitCount: number
  lastCommitGeneration: number | null
  lastCommitReason: string | null
  lastCommitVisibleCount: number
  lastFallbackReason: string | null
  firstOrderStrategy: GSplatFirstOrderUploadMode | null
  firstOrderFallbackActive: boolean
  forcedDrainActive: boolean
}

interface PendingOrderResult {
  count: number
  version: number
  sortGeneration: number
  orderData: Uint32Array
  releaseOrderData?: () => void
  receivedAtMs: number
  workerCompletedAtMs: number
  requestToSubmitLatencyMs: number | null
  skippedFrameCount: number
  textureSize: number
  estimatedBytes: number
}

interface RetiringOctreeInstance {
  instance: GSplatOctreeInstance
  retireAfterVersion: number | null
}

const DEFAULT_ORDER_UPLOAD_SCHEDULER: Required<GSplatOrderUploadSchedulerOptions> = {
  orderUploadMode: 'auto',
  minOrderUploadIntervalMs: 250,
  maxOrderUploadDelayMs: 1000,
  maxImmediateOrderUploadBytes: 2 * 1024 * 1024,
  maxOrderUploadBytesPerFrame: 2 * 1024 * 1024,
  maxOrderUploadChunksPerFrame: 1,
  firstOrderUploadMode: 'full',
  allowFirstOrderFallback: true,
  allowFullUploadFallback: true,
}

const PLAYCANVAS_ALLOCATOR_GROW_MULTIPLIER = 1.15

const INTERACTIVE_ORDER_UPLOAD_GRACE_MS = 180

function createDefaultBudgetRuntimeDiagnostics(): GSplatBudgetRuntimeDiagnostics {
  return {
    budgetScale: 1,
    globalMaxDistance: 0,
    adjustedBudget: 0,
    paddingEstimate: 0,
    rawOptimalSplats: 0,
    budgetTextureWidth: 0,
    fixedSplats: 0,
    octreeBudget: 0,
    cameraAspect: 0,
  }
}

function createDefaultOrderUploadDiagnostics(): Omit<
  GSplatOrderUploadDiagnostics,
  | 'pendingAgeMs'
  | 'pendingSortGeneration'
  | 'pendingWorldStateVersion'
  | 'pendingOrderSkippedFrameCount'
  | 'pendingRequestAgeMs'
  | 'pendingRequestToSubmitLatencyMs'
  | 'pendingWorkerToApplyLatencyMs'
  | 'stagingSortGeneration'
  | 'stagingTextureSize'
  | 'stagingVisibleCount'
> {
  return {
    scheduledUploadCount: 0,
    scheduledUploadBytes: 0,
    lastScheduledUploadBytes: 0,
    lastScheduledTextureSize: 0,
    lastScheduledAtMs: null,
    lastApplyReason: null,
    lastAppliedSortGeneration: 0,
    lastAppliedPendingOrderSkippedFrameCount: 0,
    coalescedResultCount: 0,
    staleResultCount: 0,
    observedUploadCount: 0,
    lastObservedAtMs: null,
    observedUploadAvailable: false,
    submittedRequestCount: 0,
    queuedRequestCount: 0,
    replayedRequestCount: 0,
    overwrittenRequestCount: 0,
    rejectedRequestCount: 0,
    workerErrorCount: 0,
    lastRequestToSubmitLatencyMs: null,
    lastWorkerToApplyLatencyMs: null,
    orderBufferConservativeCopyCount: 0,
    orderBufferSynchronousCopyCount: 0,
    orderBufferAllocCount: 0,
    orderBufferReuseCount: 0,
    orderBufferReleaseCount: 0,
    pendingOrderBufferLeaseCount: 0,
    adaptiveReplayDeferralCount: 0,
    adaptiveReplayTimerFireCount: 0,
    adaptiveSortIntervalMs: 0,
    workerSortTimeEmaMs: 0,
    pendingReplayDelayMs: null,
    stagingTotalBytes: 0,
    stagingSubmittedBytes: 0,
    stagingRemainingBytes: 0,
    stagingSubmittedChunks: 0,
    stagingRemainingChunks: 0,
    lastFrameSubmittedBytes: 0,
    lastFrameSubmittedChunks: 0,
    byteBudgetBlockedCount: 0,
    chunkBudgetBlockedCount: 0,
    minRowBudgetOverrideCount: 0,
    chunkedUploadCount: 0,
    fullUploadCount: 0,
    fallbackUploadCount: 0,
    abortedStagingJobCount: 0,
    activeCommitCount: 0,
    lastCommitGeneration: null,
    lastCommitReason: null,
    lastCommitVisibleCount: 0,
    lastFallbackReason: null,
    firstOrderStrategy: null,
    firstOrderFallbackActive: false,
    forcedDrainActive: false,
  }
}

function createDefaultNowMs(): () => number {
  const perf = globalThis.performance
  if (perf && typeof perf.now === 'function') {
    return () => perf.now()
  }

  return () => Date.now()
}

function normalizeOrderUploadSchedulerOptions(
  options?: GSplatOrderUploadSchedulerOptions
): GSplatOrderUploadSchedulerOptions {
  const normalized: GSplatOrderUploadSchedulerOptions = {}
  if (!options) {
    return normalized
  }

  if (options.orderUploadMode) normalized.orderUploadMode = options.orderUploadMode
  if (typeof options.minOrderUploadIntervalMs === 'number') {
    normalized.minOrderUploadIntervalMs = Math.max(0, options.minOrderUploadIntervalMs)
  }
  if (typeof options.maxOrderUploadDelayMs === 'number') {
    normalized.maxOrderUploadDelayMs = Math.max(0, options.maxOrderUploadDelayMs)
  }
  if (typeof options.maxImmediateOrderUploadBytes === 'number') {
    normalized.maxImmediateOrderUploadBytes = Math.max(0, options.maxImmediateOrderUploadBytes)
  }
  if (typeof options.maxOrderUploadBytesPerFrame === 'number') {
    normalized.maxOrderUploadBytesPerFrame = Math.max(0, options.maxOrderUploadBytesPerFrame)
  }
  if (typeof options.maxOrderUploadChunksPerFrame === 'number') {
    normalized.maxOrderUploadChunksPerFrame = Math.max(
      0,
      Math.floor(options.maxOrderUploadChunksPerFrame)
    )
  }
  if (options.firstOrderUploadMode) {
    normalized.firstOrderUploadMode = options.firstOrderUploadMode
  }
  if (typeof options.allowFirstOrderFallback === 'boolean') {
    normalized.allowFirstOrderFallback = options.allowFirstOrderFallback
  }
  if (typeof options.allowFullUploadFallback === 'boolean') {
    normalized.allowFullUploadFallback = options.allowFullUploadFallback
  }

  return normalized
}

interface GSplatScalarHistogramOptions {
  mode: GSplatScalarMode
  binCount?: number
  scale?: 'linear' | 'log'
  percentileRange?: {
    lower?: number
    upper?: number
  }
}

const scalarFilterDebugGlobal = globalThis as typeof globalThis & {
  __POTREE_GS_SCALAR_DEBUG__?: boolean
}

function isScalarFilterDebugEnabled(): boolean {
  return scalarFilterDebugGlobal.__POTREE_GS_SCALAR_DEBUG__ === true
}

function getScalarModeLabel(mode: GSplatScalarMode): string {
  switch (mode) {
    case GSplatScalarMode.Volume:
      return 'volume'
    case GSplatScalarMode.SurfaceArea:
      return 'surface-area'
    case GSplatScalarMode.Value:
      return 'value'
    default:
      return 'none'
  }
}

/**
 * GSplat 管理器
 *
 * 核心功能：
 * 1. **世界状态管理** - 版本化管理所有活跃的 Splat 实例
 * 2. **Octree 实例协调** - 管理多个八叉树实例的 LOD 评估和资源生命周期
 * 3. **相机移动检测** - 基于距离和角度触发 LOD/排序更新
 * 4. **统一排序调度** - 协调多个 Splat 的统一排序
 * 5. **颜色累积器系统** - 基于相机移动量决定球谐函数更新频率
 * 6. **资源引用计数** - 管理 Splat 资源的加载和卸载
 * 7. **Work Buffer 更新** - 协调 GPU 纹理的增量更新
 *
 * 工作流程：
 * ```
 * update() {
 *   1. 应用已完成的排序结果 → onSorted()
 *   2. 每10帧执行完整更新（检测资源加载、Octree移动）
 *   3. 检测相机移动触发LOD评估 → octreeInstances[].updateLod()
 *   4. 创建新版本的 WorldState（如果 placements 变化）
 *   5. 检测相机移动触发排序 → sort()
 *   6. 颜色累积器更新（增量SH更新）
 *   7. 批量渲染到 WorkBuffer
 * }
 * ```
 *
 * @remarks
 * 对应 PlayCanvas 的 GSplatManager 类（gsplat-manager.js）
 * 主要差异：
 * - 移除 Scene/Layer 依赖，直接管理数据流
 * - 移除 GSplatRenderer（渲染由外部材质系统处理）
 * - 移除 EventHandler（事件由外部处理）
 */
export class GSplatManager {
  // ==================== 核心组件 ====================

  /**
   * WebGL 渲染器（用于 WorkBuffer 渲染）
   */
  renderer: THREE.WebGLRenderer

  /**
   * 工作缓冲区（存储所有活跃 Splat 的 GPU 数据）
   */
  workBuffer: GSplatWorkBuffer

  /**
   * 统一排序器（处理多个 Splat 的排序）
   */
  sorter: GSplatUnifiedSorter

  /**
   * 全局参数配置
   */
  params: GSplatParams

  // ==================== 状态管理 ====================

  /**
   * 版本化的世界状态映射表
   * Key: 版本号
   * Value: 世界状态（包含所有活跃的 Splat）
   */
  worldStates = new Map<number, GSplatWorldState>()

  /**
   * 最新的世界状态版本号
   */
  lastWorldStateVersion = 0

  /**
   * 已完成排序的版本号
   */
  sortedVersion = 0

  private pendingOrder: PendingOrderResult | null = null

  private stagingOrder: PendingOrderResult | null = null

  private stagingOrderApplyReason: string | null = null

  private stagingOrderForcedDrain = false

  private lastAppliedSortGeneration = 0

  private lastOrderUploadScheduledAtMs = Number.NEGATIVE_INFINITY

  private lastInteractiveSortAtMs = Number.NEGATIVE_INFINITY

  private orderUploadScheduler: Required<GSplatOrderUploadSchedulerOptions> = {
    ...DEFAULT_ORDER_UPLOAD_SCHEDULER,
  }

  private interactiveSortScheduler: GSplatInteractiveSortSchedulerConfig | undefined

  private orderUploadNowMs: () => number = createDefaultNowMs()

  private onSortResultReady: (() => void) | null = null

  private _orderUploadDiagnostics = createDefaultOrderUploadDiagnostics()

  private orderUploadObservedProbeInstalled = false

  private observedOrderTexture: THREE.DataTexture | null = null

  private previousObservedOrderTextureOnUpdate: ((texture: THREE.Texture) => void) | null = null

  private firstWorldStateLogged = false

  private firstSortedOrderLogged = false

  private firstOrderAppliedLogged = false

  private firstWorkBufferRenderLogged = false

  private firstVisibleSplatsLogged = false

  /**
   * 拾取状态版本号。
   * 任意会导致 pick buffer 失效的变更都应该递增它。
   */
  private _pickStateVersion = 0

  /**
   * 是否需要重新排序
   */
  sortNeeded = true

  /**
   * Placement 列表已修改标志
   */
  layerPlacementsDirty = false

  /**
   * 距离下次完整更新的帧数
   * 每10帧执行一次完整更新（资源加载检测、移动检测）
   */
  framesTillFullUpdate = 0

  /**
   * WorldState 更新冷却计数器
   * 用于节流 WorldState 创建，批量累积 Placement 变化
   */
  private worldStateUpdateCooldown = 0

  // ==================== 相机状态 ====================

  /**
   * 相机节点（用于 LOD 评估和排序）
   */
  cameraNode: THREE.Object3D

  /**
   * 上次 LOD 评估时的相机位置
   */
  private lastLodCameraPos = new THREE.Vector3(Infinity, Infinity, Infinity)

  /**
   * 上次 LOD 评估时的相机前向方向
   */
  private lastLodCameraFwd = new THREE.Vector3(Infinity, Infinity, Infinity)

  /**
   * 上次 LOD 评估时的透视相机投影参数。
   */
  private lastLodCameraFov = -1
  private lastLodCameraAspect = -1

  /**
   * 上次排序时的相机位置
   */
  private lastSortCameraPos = new THREE.Vector3(Infinity, Infinity, Infinity)

  /**
   * 上次排序时的相机前向方向
   */
  private lastSortCameraFwd = new THREE.Vector3(Infinity, Infinity, Infinity)

  /**
   * 上次颜色更新时的相机位置（用于累积器）
   */
  private lastColorUpdateCameraPos = new THREE.Vector3(Infinity, Infinity, Infinity)

  /**
   * 上次颜色更新时的相机前向方向（用于累积器）
   */
  private lastColorUpdateCameraFwd = new THREE.Vector3(Infinity, Infinity, Infinity)

  // ==================== Placement 管理 ====================

  /**
   * 非 Octree 的 Placement 列表（直接渲染的 Splat）
   */
  layerPlacements: GSplatPlacement[] = []

  /**
   * Octree 实例映射表
   * Key: 根 Placement（octree placement）
   * Value: Octree 实例
   */
  octreeInstances = new Map<GSplatPlacement, GSplatOctreeInstance>()

  /**
   * 待销毁的 Octree 实例队列
   * 延迟销毁，直到不再包含该实例的新 WorldState 已完成提交
   */
  private octreeInstancesToDestroy: RetiringOctreeInstance[] = []

  /** 已退休但仍有文件处于 cooldown 的 Octree。 */
  private retiredOctrees = new Set<GSplatOctree>()

  /**
   * 标记是否有新的 Octree 实例被添加
   * 用于触发立即 LOD 评估
   */
  private hasNewOctreeInstances = false

  /** 裁切版本变化后需要立即执行一次 LOD 粗裁。 */
  private clipLodDirty = false

  /** 裁切状态实际变化次数，用于拒绝旧粗裁布局对应的排序结果。 */
  private clipLodVersion = 0

  // ==================== 临时变量（复用以避免 GC） ====================

  private _tempCameraPos = new THREE.Vector3()
  private _tempCameraRot = new THREE.Quaternion()
  private _tempCameraDir = new THREE.Vector3()
  private _tempSplatsNeedingColorUpdate: GSplatInfo[] = []
  private _tempNonOctreePlacements = new Set<GSplatPlacement>()
  private _tempOctreePlacements = new Set<GSplatPlacement>()
  private _tempOctreesTicked = new Set<GSplatOctree>()
  private _tempWorkBufferRenderedPlacements = new Set<GSplatPlacement>()
  private _tempBudgetAabbCenter = new THREE.Vector3()
  private _tempBudgetAabbSize = new THREE.Vector3()
  private _tempBudgetScale = new THREE.Vector3()

  /** WorkBuffer modifier 或 ONCE/ALWAYS 模式触发的整批刷新标记。 */
  private _workBufferDirty = false

  private budgetBalancer = new GSplatBudgetBalancer()
  private budgetScale = 1.0
  private _budgetRuntimeDiagnostics = createDefaultBudgetRuntimeDiagnostics()

  // ==================== 颜色更新参数（从 GSplatParams 同步）====================

  /**
   * 获取颜色更新距离阈值
   * 从 GSplatParams 同步，相机移动超过此距离时更新球谐函数颜色
   */
  get colorUpdateDistance(): number {
    return this.params.colorUpdateDistance
  }

  /**
   * 获取颜色更新角度阈值（度数）
   * 从 GSplatParams 同步，相机旋转超过此角度时更新球谐函数颜色
   */
  get colorUpdateAngle(): number {
    return this.params.colorUpdateAngle
  }

  /**
   * 获取颜色更新距离的 LOD 缩放因子
   * 从 GSplatParams 同步
   */
  get colorUpdateDistanceLodScale(): number {
    return this.params.colorUpdateDistanceLodScale
  }

  /**
   * 获取颜色更新角度的 LOD 缩放因子
   * 从 GSplatParams 同步
   */
  get colorUpdateAngleLodScale(): number {
    return this.params.colorUpdateAngleLodScale
  }

  /**
   * SH 更新距离阈值（相机移动超过此距离才触发 SH 重新计算）
   * 用于更精细的控制 SH 更新频率
   * @default 0.1
   */
  shUpdateDistanceThreshold = 0.05

  /**
   * SH 更新角度阈值（相机旋转超过此角度才触发 SH 重新计算，弧度）
   * 用于更精细的控制 SH 更新频率
   * @default 0.05
   */
  shUpdateAngleThreshold = 0.02

  // ==================== 排序模式配置 ====================

  /**
   * 上次排序时的相机位置（用于 Radial Sort 模式）
   */
  private lastSortPosition: THREE.Vector3 | null = null

  /**
   * 排序位置移动阈值（Radial Sort 模式下使用）
   * 与 PlayCanvas gsplat-manager.js testCameraMovedForSort() 中的 epsilon 一致
   * @default 0.001
   */
  private sortMoveThreshold: number = 0.001

  // ==================== 调试选项（从 GSplatParams 同步）====================

  /**
   * 获取 LOD 级别着色启用状态
   * 从 GSplatParams 同步
   */
  get colorizeLod(): boolean {
    return this.params.colorizeLod
  }

  /**
   * 获取颜色更新着色启用状态
   * 从 GSplatParams 同步
   */
  get colorizeColorUpdate(): boolean {
    return this.params.colorizeColorUpdate
  }

  /**
   * 获取径向排序模式
   * 从 GSplatParams 同步
   */
  get radialSort(): boolean {
    return this.params.radialSorting
  }

  // ==================== 内部标志 ====================

  /**
   * 是否已销毁
   */
  private _destroyed = false

  // ==================== 标量过滤 ====================

  /** 标量过滤是否启用 */
  private _scalarFilterEnabled = false

  /** 标量过滤模式 */
  private _scalarFilterMode: GSplatScalarMode = GSplatScalarMode.None

  /** 标量过滤最小值 */
  private _scalarFilterMin = 0

  /** 标量过滤最大值 */
  private _scalarFilterMax = 1

  /** scalarTexture 的 CPU 缓存 */
  private _scalarValues: Float32Array | null = null

  /** scalarTexture CPU 缓存对应的版本号 */
  private _scalarValuesVersion = -1

  /** scalarTexture CPU 缂撳瓨瀵瑰簲鐨勬ā寮?*/
  private _scalarValuesMode: GSplatScalarMode = GSplatScalarMode.None

  // ==================== 构造函数 ====================

  /**
   * 创建 GSplatManager
   *
   * @param renderer - Three.js WebGL 渲染器
   * @param cameraNode - 相机节点（用于 LOD 评估）
   * @param params - 全局参数配置（可选，默认使用新实例）
   */
  constructor(
    renderer: THREE.WebGLRenderer,
    cameraNode: THREE.Object3D,
    params?: GSplatParams,
    options?: GSplatManagerOptions
  ) {
    this.renderer = renderer
    this.cameraNode = cameraNode
    this.params = params ?? new GSplatParams()
    this.orderUploadScheduler = {
      ...DEFAULT_ORDER_UPLOAD_SCHEDULER,
      ...normalizeOrderUploadSchedulerOptions(options?.orderUploadScheduler),
    }
    this.interactiveSortScheduler = options?.interactiveSortScheduler
      ? { ...options.interactiveSortScheduler }
      : undefined
    this.orderUploadNowMs = options?.nowMs ?? createDefaultNowMs()
    this.onSortResultReady = options?.onSortResultReady ?? null

    // 创建核心组件
    this.workBuffer = new GSplatWorkBuffer(renderer)
    try {
      this.installOrderUploadObservedHook()
      this.sorter = this.createSorter()
    } catch (error) {
      this.restoreOrderUploadObservedHook()
      this.resetScalarValuesCache()
      this.onSortResultReady = null
      this.workBuffer.destroy()
      throw error
    }
  }

  setOrderUploadSchedulerOptions(options?: GSplatOrderUploadSchedulerOptions): void {
    this.orderUploadScheduler = {
      ...this.orderUploadScheduler,
      ...normalizeOrderUploadSchedulerOptions(options),
    }
  }

  setInteractiveSortSchedulerConfig(config?: GSplatInteractiveSortSchedulerConfig): void {
    this.interactiveSortScheduler = config ? { ...config } : undefined
    this.sorter.setInteractiveSortSchedulerConfig(this.interactiveSortScheduler)
  }

  setOnSortResultReady(callback: (() => void) | null): void {
    this.onSortResultReady = callback
  }

  // ==================== 生命周期 ====================

  /**
   * 创建排序器并绑定回调
   */
  private createSorter(): GSplatUnifiedSorter {
    const sorter = new GSplatUnifiedSorter({
      interactiveSortScheduler: this.interactiveSortScheduler,
      nowMs: this.orderUploadNowMs,
    })
    try {
      sorter.on('sorted', this.onSorted.bind(this) as OnSortedCallback)
      sorter.setOnSortJobSettledCallback(() => this.onSortResultReady?.())
      return sorter
    } catch (error) {
      sorter.destroy()
      throw error
    }
  }

  private installOrderUploadObservedHook(): void {
    const texture = this.workBuffer.orderTexture
    if (this.observedOrderTexture === texture) {
      return
    }
    if (this.observedOrderTexture) {
      this.observedOrderTexture.onUpdate = this.previousObservedOrderTextureOnUpdate ?? null
    }
    const previousOnUpdate = texture.onUpdate
    this.observedOrderTexture = texture
    this.previousObservedOrderTextureOnUpdate = previousOnUpdate
    this.orderUploadObservedProbeInstalled = true
    this._orderUploadDiagnostics.observedUploadAvailable = true
    texture.onUpdate = () => {
      previousOnUpdate?.call(texture, texture)
      this._orderUploadDiagnostics.observedUploadCount++
      this._orderUploadDiagnostics.lastObservedAtMs = this.orderUploadNowMs()
    }
  }

  private restoreOrderUploadObservedHook(): void {
    if (this.observedOrderTexture) {
      this.observedOrderTexture.onUpdate = this.previousObservedOrderTextureOnUpdate ?? null
    }
    this.observedOrderTexture = null
    this.previousObservedOrderTextureOnUpdate = null
    this.orderUploadObservedProbeInstalled = false
    this._orderUploadDiagnostics.observedUploadAvailable = false
  }

  /**
   * 销毁管理器，释放所有资源
   */
  destroy(): void {
    if (this._destroyed) return
    this._destroyed = true
    this.abortStagingOrder('destroy')
    if (this.pendingOrder) {
      this.releasePendingOrder(this.pendingOrder)
      this.pendingOrder = null
    }

    // 清理所有世界状态并减少资源引用
    for (const [, worldState] of this.worldStates) {
      this.destroyWorldState(worldState)
    }
    this.worldStates.clear()

    this._tempOctreesTicked.clear()

    // 销毁所有 Octree 实例。WorldState 已清空，可以立即释放文件资源。
    for (const [, instance] of this.octreeInstances) {
      this._tempOctreesTicked.add(instance.octree)
      instance.destroy(0)
    }
    this.octreeInstances.clear()

    // sorter 永久失败或尚未提交新版本时，待退休实例仍由 manager.destroy 兜底释放。
    for (const retirement of this.octreeInstancesToDestroy) {
      this._tempOctreesTicked.add(retirement.instance.octree)
      retirement.instance.destroy(0)
    }
    this.octreeInstancesToDestroy.length = 0

    for (const octree of this.retiredOctrees) {
      this._tempOctreesTicked.add(octree)
    }
    this.retiredOctrees.clear()
    for (const octree of this._tempOctreesTicked) {
      octree.flushCooldowns()
    }
    this._tempOctreesTicked.clear()

    // 销毁核心组件
    this.restoreOrderUploadObservedHook()
    this.resetScalarValuesCache()
    this.onSortResultReady = null
    this.workBuffer.destroy()
    this.sorter.destroy()
  }

  // ==================== Placement 协调 ====================

  /**
   * 协调 Placement 列表（外部调用，当场景中的 Splat 发生变化时）
   *
   * @param placements - 新的 Placement 列表
   *
   * @remarks
   * 对应 PlayCanvas 的 reconcile() 方法
   * 核心逻辑：
   * 1. 区分 Octree Placement 和非 Octree Placement
   * 2. 为新的 Octree Placement 创建实例
   * 3. 移除不再存在的 Octree 实例（延迟销毁）
   * 4. 检测 Placement 变化并标记 dirty
   */
  reconcile(placements: GSplatPlacement[]): void {
    this._tempNonOctreePlacements.clear()
    this._tempOctreePlacements.clear()

    // 1. 遍历新的 placements，区分 Octree 和非 Octree
    for (const p of placements) {
      if (p.octree) {
        // Octree Placement：确保实例存在
        if (!this.octreeInstances.has(p)) {
          this.octreeInstances.set(p, new GSplatOctreeInstance(p.octree, p, this.renderer))
          this.hasNewOctreeInstances = true
        }
        this._tempOctreePlacements.add(p)
      } else {
        // 非 Octree Placement：直接加入列表
        this._tempNonOctreePlacements.add(p)
      }
    }

    // 2. 移除不再存在的 Octree 实例
    for (const [placement, inst] of this.octreeInstances) {
      if (!this._tempOctreePlacements.has(placement)) {
        this.octreeInstances.delete(placement)
        this.layerPlacementsDirty = true
        this.octreeInstancesToDestroy.push({
          instance: inst,
          retireAfterVersion: null,
        })
      }
    }

    // 3. 检测非 Octree Placement 变化
    this.layerPlacementsDirty ||= this.layerPlacements.length !== this._tempNonOctreePlacements.size
    if (!this.layerPlacementsDirty) {
      for (let i = 0; i < this.layerPlacements.length; i++) {
        const existing = this.layerPlacements[i]
        if (!this._tempNonOctreePlacements.has(existing)) {
          this.layerPlacementsDirty = true
          break
        }
      }
    }

    // 4. 更新 layerPlacements
    this.layerPlacements.length = 0
    for (const p of this._tempNonOctreePlacements) {
      this.layerPlacements.push(p)
    }

    // 清理临时集合
    this._tempNonOctreePlacements.clear()
    this._tempOctreePlacements.clear()
  }

  // ==================== 世界状态管理 ====================

  /**
   * 更新世界状态（当 Placement 变化时创建新版本）
   *
   * @remarks
   * 对应 PlayCanvas 的 updateWorldState() 方法
   * 核心逻辑：
   * 1. 检测 Placement 变化
   * 2. 收集所有活跃的 Splat（非 Octree + Octree 激活节点）
   * 3. 创建新的 GSplatWorldState
   * 4. 增加资源引用计数
   * 5. 把已移除的 Octree 实例绑定到新 WorldState 版本，待该版本提交后再释放
   */
  private updateWorldState(): void {
    const worldChanged = this.layerPlacementsDirty || this.worldStates.size === 0
    if (!worldChanged) return

    this.lastWorldStateVersion++
    const splats: GSplatInfo[] = []

    // 1. 添加非 Octree Splats
    for (const p of this.layerPlacements) {
      if (p.resource) {
        // 从 node 获取 geometry（node 通常是 GaussianSplatMesh 实例）
        const geometry = hasGeometry(p.node)
          ? (p.node.geometry as IGSplatGeometry | undefined)
          : undefined
        const splatInfo = new GSplatInfo(p.resource, p, geometry)
        if (splatInfo.hasSphericalHarmonics) {
          splatInfo.resetColorAccumulators(this.colorUpdateAngle, this.colorUpdateDistance)
        }
        splats.push(splatInfo)
      }
    }

    // 2. 添加 Octree Splats（来自激活的节点）
    for (const [, inst] of this.octreeInstances) {
      inst.activePlacements.forEach((p) => {
        if (p.resource) {
          // 从 node 获取 geometry（可选）
          // 注意：对于 octree 资源，geometry 通常为 undefined，
          // 渲染时会通过 resource.configureMaterial 配置纹理
          const geometry = hasGeometry(p.node)
            ? (p.node.geometry as IGSplatGeometry | undefined)
            : undefined
          const splatInfo = new GSplatInfo(p.resource, p, geometry)
          if (splatInfo.hasSphericalHarmonics) {
            splatInfo.resetColorAccumulators(this.colorUpdateAngle, this.colorUpdateDistance)
          }
          splats.push(splatInfo)
        }
      })
    }

    // 3. 更新排序器的 centers（添加新的，移除未使用的）
    this.sorter.updateCentersForSplats(
      splats,
      this.params.sortVisibilityCompact || this.params.sortVisibilityScreenCompact
    )

    // 4. 创建新的世界状态
    const newState = new GSplatWorldState(
      this.lastWorldStateVersion,
      splats,
      8192,
      this.getBudgetTextureSizeFloor(),
      this.clipLodVersion
    )

    // 5. 增加资源引用计数（仅对支持的资源）
    for (const splat of newState.splats) {
      if (hasRefCounting(splat.resource)) {
        splat.resource.incRefCount()
      }
      this.prewarmWorkBufferShaders(splat)
    }

    this.worldStates.set(this.lastWorldStateVersion, newState)
    this.discardOrdersForOlderClipLayout(newState)
    this.assignPendingOctreeRetirementVersion(this.lastWorldStateVersion)
    this.pruneWorldStatesForUnavailableSorter(this.lastWorldStateVersion)
    if (!this.firstWorldStateLogged && newState.splats.length > 0) {
      this.firstWorldStateLogged = true
      log.debug(
        `[GSplatManager] first world state created, version=${newState.version}, splats=${newState.splats.length}, activeSplats=${newState.totalActiveSplats}, textureSize=${newState.textureSize}`
      )
    }

    this.layerPlacementsDirty = false
    this.sortNeeded = true
  }

  private destroyWorldState(worldState: GSplatWorldState): void {
    for (const splat of worldState.splats) {
      if (hasRefCounting(splat.resource)) {
        splat.resource.decRefCount()
      }
    }
    worldState.destroy()
  }

  private discardOrdersForOlderClipLayout(latestState: GSplatWorldState): void {
    const pendingState = this.pendingOrder
      ? this.worldStates.get(this.pendingOrder.version)
      : undefined
    if (
      this.pendingOrder &&
      pendingState &&
      pendingState.clipLodVersion < latestState.clipLodVersion
    ) {
      this._orderUploadDiagnostics.staleResultCount++
      this.releasePendingOrder(this.pendingOrder)
      this.pendingOrder = null
    }

    const stagingState = this.stagingOrder
      ? this.worldStates.get(this.stagingOrder.version)
      : undefined
    if (
      this.stagingOrder &&
      stagingState &&
      stagingState.clipLodVersion < latestState.clipLodVersion
    ) {
      this._orderUploadDiagnostics.staleResultCount++
      this.abortStagingOrder('stale-clip-layout')
    }
  }

  private isStaleClipLayout(worldState: GSplatWorldState): boolean {
    const latestState = this.worldStates.get(this.lastWorldStateVersion)
    return !!latestState && worldState.clipLodVersion < latestState.clipLodVersion
  }

  private pruneWorldStatesForUnavailableSorter(latestVersion: number): void {
    if (!this.sorter.isWorkerUnavailable) {
      return
    }

    for (const [version, worldState] of this.worldStates) {
      if (version === this.sortedVersion || version === latestVersion) {
        continue
      }

      if (this.pendingOrder?.version === version) {
        this.releasePendingOrder(this.pendingOrder)
        this.pendingOrder = null
      }
      if (this.stagingOrder?.version === version) {
        this.abortStagingOrder('worker-unavailable-world-state-pruned')
      }

      this.worldStates.delete(version)
      this.destroyWorldState(worldState)
    }
  }

  private assignPendingOctreeRetirementVersion(version: number): void {
    for (const retirement of this.octreeInstancesToDestroy ?? []) {
      if (retirement.retireAfterVersion === null) {
        retirement.retireAfterVersion = version
      }
    }
  }

  private retireOctreeInstancesThroughVersion(version: number): void {
    const retirements = this.octreeInstancesToDestroy
    if (!retirements || retirements.length === 0) {
      return
    }

    const pendingRetirements: RetiringOctreeInstance[] = []
    for (const retirement of retirements) {
      if (retirement.retireAfterVersion === null || retirement.retireAfterVersion > version) {
        pendingRetirements.push(retirement)
        continue
      }

      const octree = retirement.instance.octree
      retirement.instance.destroy()
      if (octree.hasPendingCooldowns) {
        this.retiredOctrees ??= new Set<GSplatOctree>()
        this.retiredOctrees.add(octree)
      }
    }
    this.octreeInstancesToDestroy = pendingRetirements
  }

  private prewarmWorkBufferShaders(splat: GSplatInfo): void {
    const resource = splat.resource
    const shBands = resource.shBands || 0
    const isSogs = resource.constructor.name === 'GSplatSogsResource' || 'meta' in resource

    const modifier = splat.placement.workBufferModifier
    this.workBuffer.prewarmRenderInfo('full', shBands, isSogs, modifier)
    this.workBuffer.prewarmRenderInfo('colorOnly', shBands, isSogs, modifier)
    if (this._scalarFilterMode !== GSplatScalarMode.None) {
      this.workBuffer.prewarmRenderInfo('scalarOnly', shBands, isSogs, modifier)
    }
  }

  setWorkBufferModifierForPlacement(
    placement: GSplatPlacement,
    modifier: CompiledGSplatWorkBufferModifier | null
  ): boolean {
    let applied = false

    for (const layerPlacement of this.layerPlacements) {
      if (layerPlacement === placement) {
        layerPlacement.workBufferModifier = modifier
        applied = true
      }
    }

    const instance = this.octreeInstances.get(placement)
    if (instance) {
      instance.setWorkBufferModifier(modifier)
      applied = true
    }

    if (applied) {
      this.markWorkBufferDirty()
    }

    return applied
  }

  /** 将最新裁切快照同步到指定八叉树实例，并合并连续动态更新。 */
  setClipStateForPlacement(placement: GSplatPlacement, state: GSplatClipState | null): boolean {
    // HIGHLIGHT only affects fragment color and never participates in coarse LOD culling. Keeping
    // it out of the octree state avoids a full tree rebuild on every right-click candidate preview.
    const cullingState = state?.volumes.some(
      (volume) =>
        volume.clipTask === ClipTask.SHOW_INSIDE || volume.clipTask === ClipTask.SHOW_OUTSIDE
    )
      ? state
      : null
    placement.clipState = cullingState
    const instance = this.octreeInstances.get(placement)
    if (!instance || !instance.setClipState(cullingState)) {
      return false
    }

    this.clipLodDirty = true
    this.clipLodVersion++
    this.framesTillFullUpdate = 0
    return true
  }

  setWorkBufferUpdateModeForPlacement(
    placement: GSplatPlacement,
    mode: GSplatWorkBufferUpdateMode
  ): boolean {
    let applied = false

    for (const layerPlacement of this.layerPlacements) {
      if (layerPlacement === placement) {
        layerPlacement.workBufferUpdateMode = mode
        applied = true
      }
    }

    const instance = this.octreeInstances.get(placement)
    if (instance) {
      instance.setWorkBufferUpdateMode(mode)
      applied = true
    }

    if (applied && mode !== GSPLAT_WORKBUFFER_UPDATE_AUTO) {
      this.markWorkBufferDirty()
    }

    return applied
  }

  markWorkBufferDirty(): void {
    this._workBufferDirty = true
  }

  private shouldForceWorkBufferRender(splats: readonly GSplatInfo[]): boolean {
    if (this._workBufferDirty) {
      return true
    }

    for (const splat of splats) {
      const mode = splat.placement.workBufferUpdateMode
      if (mode === GSPLAT_WORKBUFFER_UPDATE_ALWAYS || mode === GSPLAT_WORKBUFFER_UPDATE_ONCE) {
        return true
      }
    }

    return false
  }

  private consumeWorkBufferUpdateOnce(splats?: readonly GSplatInfo[]): void {
    const renderedPlacements = splats !== undefined ? this._tempWorkBufferRenderedPlacements : null
    if (renderedPlacements) {
      renderedPlacements.clear()
      for (const splat of splats ?? []) {
        renderedPlacements.add(splat.placement)
      }
    }

    for (const placement of this.layerPlacements) {
      if (
        (!renderedPlacements || renderedPlacements.has(placement)) &&
        placement.workBufferUpdateMode === GSPLAT_WORKBUFFER_UPDATE_ONCE
      ) {
        placement.workBufferUpdateMode = GSPLAT_WORKBUFFER_UPDATE_AUTO
      }
    }

    for (const instance of this.octreeInstances.values()) {
      instance.consumeWorkBufferUpdateOnce(renderedPlacements ?? undefined)
    }

    renderedPlacements?.clear()
  }

  getVisualActivitySnapshot(): GSplatVisualActivitySnapshot {
    const reasons: string[] = []
    const hasPlacements = this.layerPlacements.length > 0 || this.octreeInstances.size > 0

    if (
      hasPlacements &&
      (this.params.dirty ||
        this.clipLodDirty ||
        this.layerPlacementsDirty ||
        this.worldStates.size === 0)
    ) {
      reasons.push('gsplat.worldStateDirty')
    }
    if (this.canAdvanceLodPresentationWork()) {
      reasons.push('gsplat.lodPresentationWork')
    }
    if (this.hasOctreeCooldownTickWork()) {
      reasons.push('gsplat.lodCooldown')
    }
    if (hasPlacements && this.sortNeeded && !this.sorter.isWorkerUnavailable) {
      reasons.push('gsplat.sortNeeded')
    }
    if (this.pendingOrder) {
      reasons.push('gsplat.pendingOrder')
    }
    if (this.stagingOrder) {
      reasons.push('gsplat.stagingOrderUpload')
    }
    if (this._workBufferDirty) {
      reasons.push('gsplat.workBufferDirty')
    }
    if (this.hasWorkBufferUpdateMode(GSPLAT_WORKBUFFER_UPDATE_ONCE)) {
      reasons.push('gsplat.workBufferOnce')
    }
    if (this.hasWorkBufferUpdateMode(GSPLAT_WORKBUFFER_UPDATE_ALWAYS)) {
      reasons.push('gsplat.workBufferAlways')
    }

    return {
      needsNextFrame: reasons.length > 0,
      reasons,
    }
  }

  private hasWorkBufferUpdateMode(mode: GSplatWorkBufferUpdateMode): boolean {
    for (const placement of this.layerPlacements) {
      if (placement.workBufferUpdateMode === mode) {
        return true
      }
    }

    for (const instance of this.octreeInstances.values()) {
      if (instance.placement.workBufferUpdateMode === mode) {
        return true
      }
      for (const placement of instance.activePlacements) {
        if (placement.workBufferUpdateMode === mode) {
          return true
        }
      }
    }

    const sortedState = this.worldStates.get(this.sortedVersion)
    for (const splat of sortedState?.splats ?? []) {
      if (splat.placement.workBufferUpdateMode === mode) {
        return true
      }
    }

    return false
  }

  private hasLodPresentationWorkPending(): boolean {
    for (const instance of this.octreeInstances.values()) {
      if (instance.lodPresentationWorkPending) return true
    }
    return false
  }

  private hasOctreeCooldownTickWork(): boolean {
    let hasCooldownTickWork = false

    for (const instance of this.octreeInstances.values()) {
      hasCooldownTickWork ||= instance.octree?.hasCooldownTickWork === true
    }
    for (const retirement of this.octreeInstancesToDestroy ?? []) {
      hasCooldownTickWork ||= retirement.instance.octree.hasCooldownTickWork
    }
    for (const octree of this.retiredOctrees ?? []) {
      if (octree.hasPendingCooldowns) {
        hasCooldownTickWork ||= octree.hasCooldownTickWork
      } else {
        this.retiredOctrees.delete(octree)
      }
    }
    return hasCooldownTickWork
  }

  private canAdvanceLodPresentationWork(): boolean {
    return this.sorter.jobsInFlight < 3 && this.hasLodPresentationWorkPending()
  }

  private releasePendingOrder(pending: PendingOrderResult): void {
    pending.releaseOrderData?.()
  }

  private abortStagingOrder(reason: string): void {
    if (!this.stagingOrder) {
      return
    }

    this.workBuffer.abortOrderStaging()
    this.releasePendingOrder(this.stagingOrder)
    this.stagingOrder = null
    this.stagingOrderApplyReason = null
    this.stagingOrderForcedDrain = false
    this._orderUploadDiagnostics.abortedStagingJobCount =
      (this._orderUploadDiagnostics.abortedStagingJobCount ?? 0) + 1
    this._orderUploadDiagnostics.lastFallbackReason = reason
  }

  private estimateOrderUploadBytes(
    visibleCount: number,
    textureSize: number,
    orderDataLength: number
  ): number {
    const width = Math.max(1, Math.floor(textureSize))
    const validElements = Math.min(
      Math.max(0, Math.floor(visibleCount)),
      Math.max(0, orderDataLength),
      width * width
    )
    if (validElements === 0) {
      return 0
    }
    return Math.ceil(validElements / width) * width * Uint32Array.BYTES_PER_ELEMENT
  }

  /**
   * 排序完成回调（由 Sorter 调用）
   *
   * @param _count - 渲染的 splat 数量
   * @param version - 世界状态版本号
   * @param orderData - 排序索引数据
   *
   * @remarks
   * 对应 PlayCanvas 的 onSorted() 方法
   * 核心逻辑：
   * 1. 清理旧版本的世界状态（减少资源引用）
   * 2. 首次排序：完整更新 WorkBuffer
   * 3. 更新 order 纹理
   */
  private onSorted(
    count: number,
    version: number,
    orderData: Uint32Array,
    sortGeneration: number = 0,
    metadata?: SortResultMetadata
  ): void {
    const generation = Number.isFinite(sortGeneration) ? Math.max(0, Math.floor(sortGeneration)) : 0
    const releaseOrderData = metadata?.releaseOrderData

    if (generation > 0 && generation <= this.lastAppliedSortGeneration) {
      this._orderUploadDiagnostics.staleResultCount++
      releaseOrderData?.()
      return
    }

    const pendingWorldState = this.worldStates.get(version)
    if (
      !pendingWorldState ||
      version < this.sortedVersion ||
      this.isStaleClipLayout(pendingWorldState)
    ) {
      this._orderUploadDiagnostics.staleResultCount++
      releaseOrderData?.()
      if (!pendingWorldState) {
        log.warn(`GSplatManager: World state with version ${version} not found`)
      }
      return
    }

    if (this.stagingOrder && generation > 0 && this.stagingOrder.sortGeneration >= generation) {
      this._orderUploadDiagnostics.staleResultCount++
      releaseOrderData?.()
      return
    }

    if (this.pendingOrder && generation > 0 && this.pendingOrder.sortGeneration >= generation) {
      this._orderUploadDiagnostics.staleResultCount++
      releaseOrderData?.()
      return
    }

    const previousSkippedFrameCount = this.pendingOrder?.skippedFrameCount ?? 0
    if (this.stagingOrder && !this.stagingOrderForcedDrain) {
      this._orderUploadDiagnostics.coalescedResultCount++
      this.abortStagingOrder('new-generation')
    }
    if (this.pendingOrder) {
      this._orderUploadDiagnostics.coalescedResultCount++
      this.releasePendingOrder(this.pendingOrder)
    }

    const pendingTextureSize = pendingWorldState.textureSize
    const safeOrderData = releaseOrderData ? orderData : new Uint32Array(orderData)
    if (!releaseOrderData) {
      this._orderUploadDiagnostics.orderBufferConservativeCopyCount++
    }

    this.pendingOrder = {
      count,
      version,
      sortGeneration: generation,
      orderData: safeOrderData,
      releaseOrderData,
      receivedAtMs: this.orderUploadNowMs(),
      workerCompletedAtMs: metadata?.completedAtMs ?? this.orderUploadNowMs(),
      requestToSubmitLatencyMs: metadata?.requestToSubmitLatencyMs ?? null,
      skippedFrameCount: previousSkippedFrameCount,
      textureSize: pendingTextureSize,
      estimatedBytes: this.estimateOrderUploadBytes(
        count,
        pendingTextureSize,
        safeOrderData.length
      ),
    }
    this._orderUploadDiagnostics.lastRequestToSubmitLatencyMs =
      metadata?.requestToSubmitLatencyMs ??
      this._orderUploadDiagnostics.lastRequestToSubmitLatencyMs
    if (!this.firstSortedOrderLogged) {
      this.firstSortedOrderLogged = true
      log.debug(
        `[GSplatManager] first sorted order received, version=${version}, generation=${generation}, visibleSplats=${count}, textureSize=${pendingTextureSize}, workerTime=${metadata?.workerTimeMs?.toFixed(1) ?? 'unknown'}ms`
      )
    }
  }

  // ==================== 相机移动检测 ====================

  /**
   * 检测相机移动是否需要触发 LOD 更新
   *
   * @returns true 表示相机移动超过阈值，需要重新评估 LOD
   *
   * @remarks
   * 对应 PlayCanvas 的 testCameraMovedForLod() 方法
   * 检测条件：
   * 1. 距离检测：相机位置移动超过 lodUpdateDistance
   * 2. 旋转检测（可选）：相机旋转超过 lodUpdateAngle
   */
  private tryApplyPendingOrder(): boolean {
    if (this.stagingOrder) {
      if (this.shouldPrioritizeInteractiveOrderUpload(this.stagingOrder)) {
        return this.fallbackStagingOrderToFullUpload('interactive-sort')
      }
      return this.advanceStagingOrderUpload()
    }

    if (!this.pendingOrder) {
      return false
    }

    const decision = this.getPendingOrderApplyDecision()
    if (!decision) {
      this.pendingOrder.skippedFrameCount++
      return false
    }

    if (decision.mode === 'full') {
      return this.applyPendingOrderFullUpload(decision.reason)
    }

    return this.beginPendingOrderStaging(decision.reason, decision.forcedDrain)
  }

  private getPendingOrderApplyDecision(): {
    reason: string
    mode: 'full' | 'chunked'
    forcedDrain: boolean
  } | null {
    const pending = this.pendingOrder
    if (!pending) {
      return null
    }

    if (this.sortedVersion === 0) {
      const firstOrderMode = this.orderUploadScheduler.firstOrderUploadMode
      this._orderUploadDiagnostics.firstOrderStrategy = firstOrderMode
      if (firstOrderMode === 'chunked-with-fallback') {
        if (!this.orderUploadScheduler.allowFirstOrderFallback) {
          return { reason: 'first-result', mode: 'full', forcedDrain: false }
        }
        this._orderUploadDiagnostics.firstOrderFallbackActive = true
        return { reason: 'first-result-fallback', mode: 'chunked', forcedDrain: true }
      }
      return { reason: 'first-result', mode: 'full', forcedDrain: false }
    }

    if (this.shouldPrioritizeInteractiveOrderUpload(pending)) {
      return { reason: 'interactive-sort', mode: 'full', forcedDrain: false }
    }

    const now = this.orderUploadNowMs()
    const ageMs = Math.max(0, now - pending.receivedAtMs)
    let reason: string | null = null
    let forcedDrain = false
    if (ageMs >= this.orderUploadScheduler.maxOrderUploadDelayMs) {
      reason = 'max-delay'
      forcedDrain = true
    }

    const maxImmediateBytes = this.orderUploadScheduler.maxImmediateOrderUploadBytes
    if (!reason && maxImmediateBytes >= 0 && pending.estimatedBytes <= maxImmediateBytes) {
      reason = 'immediate-size'
    }

    const elapsedMs = now - this.lastOrderUploadScheduledAtMs
    if (!reason && elapsedMs >= this.orderUploadScheduler.minOrderUploadIntervalMs) {
      reason = 'interval'
    }

    if (!reason) {
      return null
    }

    const mode = this.getOrderUploadModeForPending(pending)
    return { reason, mode, forcedDrain }
  }

  private shouldPrioritizeInteractiveOrderUpload(pending: PendingOrderResult): boolean {
    return (
      this.orderUploadScheduler.orderUploadMode === 'auto' &&
      (this.sortNeeded ||
        (this.sorter?.jobsInFlight ?? 0) > 0 ||
        this.hasPendingSortReplayRequest() ||
        pending.skippedFrameCount > 0 ||
        this.hasRecentInteractiveSortActivity()) &&
      pending.sortGeneration > 0
    )
  }

  private hasPendingSortReplayRequest(): boolean {
    return this.sorter?.diagnostics?.pendingRequestAgeMs != null
  }

  private markInteractiveSortActivity(): void {
    this.lastInteractiveSortAtMs = this.getOrderUploadNowMs()
  }

  private hasRecentInteractiveSortActivity(): boolean {
    return (
      this.getOrderUploadNowMs() - this.lastInteractiveSortAtMs < INTERACTIVE_ORDER_UPLOAD_GRACE_MS
    )
  }

  private getOrderUploadNowMs(): number {
    return typeof this.orderUploadNowMs === 'function' ? this.orderUploadNowMs() : 0
  }

  private getOrderUploadModeForPending(pending: PendingOrderResult): 'full' | 'chunked' {
    const mode = this.orderUploadScheduler.orderUploadMode
    if (mode === 'full') {
      return 'full'
    }
    if (mode === 'chunked') {
      return 'chunked'
    }
    if (pending.estimatedBytes <= this.orderUploadScheduler.maxImmediateOrderUploadBytes) {
      return 'full'
    }
    return 'chunked'
  }

  private applyPendingOrderFullUpload(reason: string): boolean {
    const pending = this.pendingOrder
    if (!pending) {
      return false
    }
    this.pendingOrder = null

    if (
      (pending.sortGeneration > 0 && pending.sortGeneration <= this.lastAppliedSortGeneration) ||
      pending.version < this.sortedVersion
    ) {
      this._orderUploadDiagnostics.staleResultCount++
      this.releasePendingOrder(pending)
      return false
    }

    const worldState = this.worldStates.get(pending.version)
    if (!worldState || this.isStaleClipLayout(worldState)) {
      this._orderUploadDiagnostics.staleResultCount++
      this.releasePendingOrder(pending)
      if (!worldState) {
        log.warn(`GSplatManager: World state with version ${pending.version} not found`)
      }
      return false
    }

    worldState.sortedVisibleSplatCount = Math.min(
      worldState.totalActiveSplats,
      Math.max(0, Math.floor(pending.count))
    )

    if (worldState.textureSize !== this.workBuffer.textureSize) {
      this.workBuffer.resize(worldState.textureSize)
    }

    const orderUploadResult = this.workBuffer.setOrderData(pending.orderData) ?? {
      synchronousCopy: true,
      conservativeCopy: false,
      copiedBytes: pending.estimatedBytes,
      textureRecreated: false,
      textureSizeChanged: false,
      mode: 'full' as const,
    }
    if (orderUploadResult.synchronousCopy) {
      this._orderUploadDiagnostics.orderBufferSynchronousCopyCount++
    }
    if (orderUploadResult.conservativeCopy) {
      this._orderUploadDiagnostics.orderBufferConservativeCopyCount++
    }
    this.releasePendingOrder(pending)
    this._orderUploadDiagnostics.fullUploadCount =
      (this._orderUploadDiagnostics.fullUploadCount ?? 0) + 1

    return this.commitAppliedOrder(pending, worldState, orderUploadResult, reason)
  }

  private beginPendingOrderStaging(reason: string, forcedDrain: boolean): boolean {
    const pending = this.pendingOrder
    if (!pending) {
      return false
    }
    this.pendingOrder = null

    if (
      (pending.sortGeneration > 0 && pending.sortGeneration <= this.lastAppliedSortGeneration) ||
      pending.version < this.sortedVersion
    ) {
      this._orderUploadDiagnostics.staleResultCount++
      this.releasePendingOrder(pending)
      return false
    }

    const worldState = this.worldStates.get(pending.version)
    if (!worldState || this.isStaleClipLayout(worldState)) {
      this._orderUploadDiagnostics.staleResultCount++
      this.releasePendingOrder(pending)
      if (!worldState) {
        log.warn(`GSplatManager: World state with version ${pending.version} not found`)
      }
      return false
    }

    const visibleCount = Math.min(
      worldState.totalActiveSplats,
      Math.max(0, Math.floor(pending.count))
    )

    this.workBuffer.beginOrderStaging(pending.orderData, {
      textureSize: worldState.textureSize,
      visibleCount,
      generation: pending.sortGeneration,
    })
    this.stagingOrder = pending
    this.stagingOrderApplyReason = reason
    this.stagingOrderForcedDrain = forcedDrain
    this._orderUploadDiagnostics.chunkedUploadCount =
      (this._orderUploadDiagnostics.chunkedUploadCount ?? 0) + 1
    this._orderUploadDiagnostics.forcedDrainActive = forcedDrain
    this.updateStagingDiagnostics()

    return this.advanceStagingOrderUpload()
  }

  private advanceStagingOrderUpload(): boolean {
    const pending = this.stagingOrder
    if (!pending) {
      return false
    }

    const result = this.workBuffer.advanceOrderStaging({
      maxBytesPerFrame: this.orderUploadScheduler.maxOrderUploadBytesPerFrame,
      maxChunksPerFrame: this.orderUploadScheduler.maxOrderUploadChunksPerFrame,
    })

    this.recordStagingAdvanceDiagnostics(result)

    if (result.fallbackToFullUpload) {
      if (!this.orderUploadScheduler.allowFullUploadFallback) {
        this.abortStagingOrder(result.fallbackReason ?? 'chunked-upload-failed')
        return false
      }
      return this.fallbackStagingOrderToFullUpload(result.fallbackReason ?? 'chunked-upload-failed')
    }

    if (!result.completed) {
      return result.submittedChunks > 0
    }

    return this.commitStagingOrder()
  }

  private drainStagingOrderUpload(reason: string): boolean {
    if (!this.stagingOrder) {
      return false
    }
    this.stagingOrderApplyReason = reason
    this.stagingOrderForcedDrain = true
    this._orderUploadDiagnostics.forcedDrainActive = true

    let guard = 0
    while (this.stagingOrder && guard < 1024) {
      const result = this.workBuffer.advanceOrderStaging({
        maxBytesPerFrame: Number.POSITIVE_INFINITY,
        maxChunksPerFrame: Number.MAX_SAFE_INTEGER,
      })
      this.recordStagingAdvanceDiagnostics(result)
      if (result.fallbackToFullUpload) {
        return this.fallbackStagingOrderToFullUpload(result.fallbackReason ?? reason)
      }
      if (result.completed) {
        return this.commitStagingOrder()
      }
      guard++
    }

    return false
  }

  private fallbackStagingOrderToFullUpload(reason: string): boolean {
    const pending = this.stagingOrder
    if (!pending) {
      return false
    }
    this.workBuffer.abortOrderStaging()
    this.stagingOrder = null
    this.stagingOrderApplyReason = null
    this.stagingOrderForcedDrain = false
    this.pendingOrder = pending
    this._orderUploadDiagnostics.fallbackUploadCount =
      (this._orderUploadDiagnostics.fallbackUploadCount ?? 0) + 1
    this._orderUploadDiagnostics.lastFallbackReason = reason
    this._orderUploadDiagnostics.forcedDrainActive = false
    return this.applyPendingOrderFullUpload(`fallback:${reason}`)
  }

  private updateStagingDiagnostics(): void {
    const info = this.workBuffer.getOrderStagingInfo()
    this._orderUploadDiagnostics.stagingTotalBytes = info?.totalBytes ?? 0
    this._orderUploadDiagnostics.stagingSubmittedBytes = info?.submittedBytes ?? 0
    this._orderUploadDiagnostics.stagingRemainingBytes = info?.remainingBytes ?? 0
    this._orderUploadDiagnostics.stagingSubmittedChunks = info?.submittedChunks ?? 0
    this._orderUploadDiagnostics.stagingRemainingChunks = info?.remainingChunks ?? 0
  }

  private recordStagingAdvanceDiagnostics(result: GSplatWorkBufferAdvanceOrderStagingResult): void {
    this._orderUploadDiagnostics.lastFrameSubmittedBytes = result.submittedBytes
    this._orderUploadDiagnostics.lastFrameSubmittedChunks = result.submittedChunks
    if (result.blockedReason === 'byte-budget') {
      this._orderUploadDiagnostics.byteBudgetBlockedCount =
        (this._orderUploadDiagnostics.byteBudgetBlockedCount ?? 0) + 1
    } else if (result.blockedReason === 'chunk-budget') {
      this._orderUploadDiagnostics.chunkBudgetBlockedCount =
        (this._orderUploadDiagnostics.chunkBudgetBlockedCount ?? 0) + 1
    }
    if (result.minRowBudgetOverride) {
      this._orderUploadDiagnostics.minRowBudgetOverrideCount =
        (this._orderUploadDiagnostics.minRowBudgetOverrideCount ?? 0) + 1
    }
    if (result.fallbackReason) {
      this._orderUploadDiagnostics.lastFallbackReason = result.fallbackReason
    }
    this.updateStagingDiagnostics()
  }

  private clearStagingDiagnostics(): void {
    this._orderUploadDiagnostics.stagingTotalBytes = 0
    this._orderUploadDiagnostics.stagingSubmittedBytes = 0
    this._orderUploadDiagnostics.stagingRemainingBytes = 0
    this._orderUploadDiagnostics.stagingSubmittedChunks = 0
    this._orderUploadDiagnostics.stagingRemainingChunks = 0
  }

  private commitStagingOrder(): boolean {
    const pending = this.stagingOrder
    if (!pending) {
      return false
    }
    const worldState = this.worldStates.get(pending.version)
    if (!worldState || this.isStaleClipLayout(worldState)) {
      this._orderUploadDiagnostics.staleResultCount++
      this.abortStagingOrder(worldState ? 'stale-clip-layout' : 'missing-world-state')
      return false
    }

    worldState.sortedVisibleSplatCount = Math.min(
      worldState.totalActiveSplats,
      Math.max(0, Math.floor(pending.count))
    )
    const orderUploadResult = this.workBuffer.commitOrderStaging()
    this.installOrderUploadObservedHook()
    this.releasePendingOrder(pending)

    this.stagingOrder = null
    const reason = this.stagingOrderApplyReason ?? 'chunked'
    this.stagingOrderApplyReason = null
    this.stagingOrderForcedDrain = false
    this._orderUploadDiagnostics.firstOrderFallbackActive = false
    this._orderUploadDiagnostics.forcedDrainActive = false

    return this.commitAppliedOrder(pending, worldState, orderUploadResult, reason)
  }

  private commitAppliedOrder(
    pending: PendingOrderResult,
    worldState: GSplatWorldState,
    orderUploadResult: GSplatWorkBufferOrderUploadResult,
    reason: string
  ): boolean {
    for (let v = this.sortedVersion; v < pending.version; v++) {
      const oldState = this.worldStates.get(v)!
      if (oldState) {
        this.worldStates.delete(v)
        this.destroyWorldState(oldState)
      }
    }

    this.sortedVersion = pending.version
    this.retireOctreeInstancesThroughVersion(pending.version)
    if (pending.sortGeneration > 0) {
      this.lastAppliedSortGeneration = pending.sortGeneration
    }
    this.bumpPickStateVersion()
    if (!this.firstOrderAppliedLogged) {
      this.firstOrderAppliedLogged = true
      log.debug(
        `[GSplatManager] first order texture applied, version=${pending.version}, generation=${pending.sortGeneration}, visibleSplats=${worldState.sortedVisibleSplatCount}, mode=${orderUploadResult.mode}, bytes=${orderUploadResult.copiedBytes || pending.estimatedBytes}, reason=${reason}`
      )
    }

    if (!worldState.sortedBefore) {
      worldState.sortedBefore = true

      this.workBuffer.render(worldState.splats, this.cameraNode, this.getDebugColors())
      if (!this.firstWorkBufferRenderLogged) {
        this.firstWorkBufferRenderLogged = true
        log.debug(
          `[GSplatManager] first workBuffer render completed, version=${pending.version}, splats=${worldState.splats.length}, visibleSplats=${worldState.sortedVisibleSplatCount}`
        )
      }

      worldState.splats.forEach((splat) => {
        splat.update()
        if (splat.hasSphericalHarmonics) {
          splat.resetColorAccumulators(this.colorUpdateAngle, this.colorUpdateDistance)
        }
      })

      if (this._scalarFilterMode !== GSplatScalarMode.None) {
        this.workBuffer.markScalarDirty()
      }

      this._workBufferDirty = false
      this.consumeWorkBufferUpdateOnce(worldState.splats)
      this.updateColorCameraTracking()
    }

    const now = this.orderUploadNowMs()
    const workerToApplyLatencyMs = Math.max(0, now - pending.workerCompletedAtMs)
    const scheduledBytes = orderUploadResult.copiedBytes || pending.estimatedBytes
    this.lastOrderUploadScheduledAtMs = now
    this._orderUploadDiagnostics.scheduledUploadCount++
    this._orderUploadDiagnostics.scheduledUploadBytes += scheduledBytes
    this._orderUploadDiagnostics.lastScheduledUploadBytes = scheduledBytes
    this._orderUploadDiagnostics.lastScheduledTextureSize = worldState.textureSize
    this._orderUploadDiagnostics.lastScheduledAtMs = now
    this._orderUploadDiagnostics.lastApplyReason = reason
    this._orderUploadDiagnostics.lastAppliedSortGeneration = this.lastAppliedSortGeneration
    this._orderUploadDiagnostics.lastWorkerToApplyLatencyMs = workerToApplyLatencyMs
    this._orderUploadDiagnostics.lastAppliedPendingOrderSkippedFrameCount =
      pending.skippedFrameCount
    this._orderUploadDiagnostics.activeCommitCount =
      (this._orderUploadDiagnostics.activeCommitCount ?? 0) + 1
    this._orderUploadDiagnostics.lastCommitGeneration = pending.sortGeneration
    this._orderUploadDiagnostics.lastCommitReason = reason
    this._orderUploadDiagnostics.lastCommitVisibleCount = worldState.sortedVisibleSplatCount
    this.clearStagingDiagnostics()

    return true
  }

  flushPendingOrderUpload(reason: string = 'flush'): boolean {
    if (this.stagingOrder) {
      return this.drainStagingOrderUpload(reason)
    }
    return this.applyPendingOrderFullUpload(reason)
  }

  private computeGlobalMaxDistance(): number {
    let maxDistance = 0
    this.cameraNode.updateMatrixWorld(true)
    this._tempCameraPos.setFromMatrixPosition(this.cameraNode.matrixWorld)

    for (const [, inst] of this.octreeInstances) {
      const node = inst.placement.node
      node.updateMatrixWorld(true)
      const matrixWorld = node.matrixWorld
      inst.placement.aabb.getCenter(this._tempBudgetAabbCenter).applyMatrix4(matrixWorld)
      inst.placement.aabb.getSize(this._tempBudgetAabbSize)
      this._tempBudgetScale.setFromMatrixScale(matrixWorld)
      const uniformScale = Math.max(
        this._tempBudgetScale.x,
        this._tempBudgetScale.y,
        this._tempBudgetScale.z
      )
      const radius = this._tempBudgetAabbSize.length() * 0.5 * uniformScale
      const distance = this._tempBudgetAabbCenter.distanceTo(this._tempCameraPos) + radius
      if (distance > maxDistance) {
        maxDistance = distance
      }
    }

    return Math.max(maxDistance, 1)
  }

  private getBudgetTextureSizeFloor(): number {
    const budget = Math.max(0, Math.floor(this.params.maxCachePoints))
    if (budget <= 0) {
      return 1
    }

    const allocatorCapacity = Math.ceil(budget * PLAYCANVAS_ALLOCATOR_GROW_MULTIPLIER)
    return Math.max(1, Math.ceil(Math.sqrt(allocatorCapacity)))
  }

  private enforceBudget(budget: number): void {
    const textureWidth = Math.max(1, this.workBuffer.textureSize, this.getBudgetTextureSizeFloor())
    let fixedSplats = 0
    let paddingEstimate = 0

    for (const placement of this.layerPlacements) {
      const resource = placement.resource
      if (resource) {
        const numSplats = resource.numSplats ?? 0
        fixedSplats += numSplats
        paddingEstimate += (textureWidth - (numSplats % textureWidth)) % textureWidth
      }
    }

    const octreeBudget = Math.max(1, budget - fixedSplats)
    const globalMaxDistance = this.computeGlobalMaxDistance()

    let totalOptimalSplats = 0
    for (const [, inst] of this.octreeInstances) {
      totalOptimalSplats += inst.evaluateOptimalLods(
        this.cameraNode,
        this.params,
        this.budgetScale,
        globalMaxDistance
      )
      for (const placement of inst.activePlacements) {
        const resource = placement.resource
        const numSplats = resource?.numSplats ?? 0
        paddingEstimate += (textureWidth - (numSplats % textureWidth)) % textureWidth
      }
    }

    const adjustedBudget = Math.max(1, octreeBudget - paddingEstimate)
    if (totalOptimalSplats > 0) {
      const ratio = totalOptimalSplats / adjustedBudget
      const deadZone = 0.4
      const blendRate = 0.3
      if (ratio > 1 + deadZone || ratio < 1 - deadZone) {
        const invCorrection = 1 / Math.sqrt(ratio)
        this.budgetScale *= 1 + (invCorrection - 1) * blendRate
        this.budgetScale = Math.max(0.01, Math.min(this.budgetScale, 100.0))
      }
    }

    const camera = this.cameraNode as THREE.PerspectiveCamera
    this._budgetRuntimeDiagnostics = {
      budgetScale: this.budgetScale,
      globalMaxDistance,
      adjustedBudget,
      paddingEstimate,
      rawOptimalSplats: totalOptimalSplats,
      budgetTextureWidth: textureWidth,
      fixedSplats,
      octreeBudget,
      cameraAspect: camera.isPerspectiveCamera ? camera.aspect : 0,
    }

    this.budgetBalancer.balance(this.octreeInstances, adjustedBudget)

    for (const [, inst] of this.octreeInstances) {
      inst.applyLodChanges(inst.octree.lodLevels - 1, this.params)
    }
  }

  private testCameraMovedForLod(): boolean {
    // 获取当前相机位置
    this.cameraNode.updateMatrixWorld(true)
    this._tempCameraPos.setFromMatrixPosition(this.cameraNode.matrixWorld)

    // 距离检测
    const distanceThreshold = this.params.lodUpdateDistance
    const cameraMoved = this.lastLodCameraPos.distanceTo(this._tempCameraPos) > distanceThreshold
    if (cameraMoved) {
      return true
    }

    // 旋转检测（可选）
    const lodUpdateAngleDeg = this.params.lodUpdateAngle
    if (lodUpdateAngleDeg > 0) {
      if (Number.isFinite(this.lastLodCameraFwd.x)) {
        // 获取当前相机前向方向
        this._tempCameraRot.setFromRotationMatrix(this.cameraNode.matrixWorld)
        this._tempCameraDir.set(0, 0, -1).applyQuaternion(this._tempCameraRot)

        // 计算角度差
        const dot = Math.min(1, Math.max(-1, this.lastLodCameraFwd.dot(this._tempCameraDir)))
        const angle = Math.acos(dot)
        const rotThreshold = lodUpdateAngleDeg * THREE.MathUtils.DEG2RAD
        const cameraRotated = angle > rotThreshold
        if (cameraRotated) {
          return true
        }
      } else {
        // 首次运行，强制更新
        return true
      }
    }

    const camera = this.cameraNode as THREE.PerspectiveCamera
    if (camera.isPerspectiveCamera) {
      const fovChanged =
        this.lastLodCameraFov < 0 ||
        Math.abs(camera.fov - this.lastLodCameraFov) > Math.max(0.001, this.lastLodCameraFov * 0.02)
      const aspectChanged =
        this.lastLodCameraAspect < 0 ||
        Math.abs(camera.aspect - this.lastLodCameraAspect) >
          Math.max(0.001, this.lastLodCameraAspect * 0.02)
      if (fovChanged || aspectChanged) {
        return true
      }
    }

    return false
  }

  /**
   * 检测相机移动是否需要触发排序
   *
   * @returns true 表示相机移动超过阈值，需要重新排序
   *
   * @remarks
   * 对应 PlayCanvas 的 testCameraMovedForSort() 方法
   * 排序模式差异：
   * - Radial Sort（径向排序）：仅位置变化影响排序
   * - Linear Sort（方向排序）：仅方向变化影响排序
   */
  private testCameraMovedForSort(): boolean {
    const epsilon = 0.001

    // 获取当前相机状态
    this.cameraNode.updateMatrixWorld(true)
    this._tempCameraPos.setFromMatrixPosition(this.cameraNode.matrixWorld)

    if (this.radialSort) {
      // Radial Sort: 检测相机位置变化
      if (!this.lastSortPosition) {
        return true
      }

      if (this._tempCameraPos.distanceTo(this.lastSortPosition) > this.sortMoveThreshold) {
        return true
      }

      if (!this.params.sortVisibilityCompact) {
        return false
      }

      this._tempCameraRot.setFromRotationMatrix(this.cameraNode.matrixWorld)
      this._tempCameraDir.set(0, 0, -1).applyQuaternion(this._tempCameraRot)

      if (!Number.isFinite(this.lastSortCameraFwd.x)) {
        return true
      }

      const dot = Math.min(1, Math.max(-1, this.lastSortCameraFwd.dot(this._tempCameraDir)))
      const refreshAngleRad = Math.max(
        epsilon,
        this.params.sortVisibilityRotationRefreshAngle * THREE.MathUtils.DEG2RAD
      )
      return Math.acos(dot) > refreshAngleRad
    } else {
      // Linear Sort: 检测相机方向变化
      this._tempCameraRot.setFromRotationMatrix(this.cameraNode.matrixWorld)
      this._tempCameraDir.set(0, 0, -1).applyQuaternion(this._tempCameraRot)

      if (!Number.isFinite(this.lastSortCameraFwd.x)) {
        return true
      }

      const dot = Math.min(1, Math.max(-1, this.lastSortCameraFwd.dot(this._tempCameraDir)))
      const angle = Math.acos(dot)
      if (angle > epsilon) {
        return true
      }

      if (!this.params.sortVisibilityCompact) {
        return false
      }

      if (!Number.isFinite(this.lastSortCameraPos.x)) {
        return true
      }

      return this._tempCameraPos.distanceTo(this.lastSortCameraPos) > this.sortMoveThreshold
    }
  }

  /**
   * 更新颜色相机追踪状态（用于累积器计算）
   */
  private updateColorCameraTracking(): void {
    this.cameraNode.updateMatrixWorld(true)
    this._tempCameraPos.setFromMatrixPosition(this.cameraNode.matrixWorld)
    this._tempCameraRot.setFromRotationMatrix(this.cameraNode.matrixWorld)
    this._tempCameraDir.set(0, 0, -1).applyQuaternion(this._tempCameraRot)

    this.lastColorUpdateCameraPos.copy(this._tempCameraPos)
    this.lastColorUpdateCameraFwd.copy(this._tempCameraDir)
  }

  /**
   * 记录已成功提交排序任务时的相机状态。
   */
  private updateLastSortCameraTracking(): void {
    this.cameraNode.updateMatrixWorld(true)
    this._tempCameraPos.setFromMatrixPosition(this.cameraNode.matrixWorld)
    this._tempCameraRot.setFromRotationMatrix(this.cameraNode.matrixWorld)
    this._tempCameraDir.set(0, 0, -1).applyQuaternion(this._tempCameraRot)

    this.lastSortCameraPos.copy(this._tempCameraPos)
    this.lastSortCameraFwd.copy(this._tempCameraDir)

    if (!this.lastSortPosition) {
      this.lastSortPosition = new THREE.Vector3()
    }
    this.lastSortPosition.copy(this._tempCameraPos)
  }

  /**
   * 计算相机移动增量（用于颜色累积器）
   *
   * @returns 旋转增量（度数）和平移增量（世界单位）
   *
   * @remarks
   * 对应 PlayCanvas 的 calculateColorCameraDeltas() 方法
   */
  private calculateColorCameraDeltas(): { rotationDelta: number; translationDelta: number } {
    let rotationDelta = 0
    let translationDelta = 0

    // 跳过首帧（相机位置未初始化）
    if (Number.isFinite(this.lastColorUpdateCameraPos.x)) {
      // 获取当前相机状态
      this.cameraNode.updateMatrixWorld(true)
      this._tempCameraPos.setFromMatrixPosition(this.cameraNode.matrixWorld)
      this._tempCameraRot.setFromRotationMatrix(this.cameraNode.matrixWorld)
      this._tempCameraDir.set(0, 0, -1).applyQuaternion(this._tempCameraRot)

      // 计算旋转增量（度数）
      const dot = Math.min(1, Math.max(-1, this.lastColorUpdateCameraFwd.dot(this._tempCameraDir)))
      rotationDelta = Math.acos(dot) * THREE.MathUtils.RAD2DEG

      // 计算平移增量
      translationDelta = this.lastColorUpdateCameraPos.distanceTo(this._tempCameraPos)
    }

    return { rotationDelta, translationDelta }
  }

  // ==================== 排序 ====================

  /**
   * 触发排序（将所有 Splat 按相机视角排序）
   *
   * @param lastState - 最新的世界状态
   *
   * @remarks
   * 对应 PlayCanvas 的 sort() 方法
   * 核心逻辑：
   * 1. 获取相机世界空间属性（位置、方向）
   * 2. 对每个 Splat 计算排序参数（变换到相机空间）
   * 3. 调用 Sorter 进行排序
   */
  private sort(lastState: GSplatWorldState): SortRequestResult {
    const camera = this.cameraNode as THREE.Camera
    return this.sorter.setSortParams(camera, lastState.splats, {
      enabled: this.params.sortVisibilityCompact,
      behindMargin: this.params.sortVisibilityBehindMargin,
      screenCompact: this.params.sortVisibilityScreenCompact,
      screenMargin: this.params.sortVisibilityScreenMargin,
    })
  }

  private handleSortRequestResult(result: SortRequestResult): boolean {
    if (result.retrySuppressed) {
      // 同一参数仍在退避或已熔断；等待 sorter 定时唤醒或新 WorldState，
      // 避免 sortNeeded 让渲染循环逐帧原样提交。
      this.sortNeeded = false
      return false
    }

    if (result.status !== 'submitted' && result.status !== 'queued') {
      return false
    }

    this.markInteractiveSortActivity()
    this.sortNeeded = false
    this.updateLastSortCameraTracking()
    return true
  }

  /**
   * 准备排序参数（传递给 Sorter）
   *
   * @param worldState - 世界状态
   * @returns 排序参数负载
   *
   * @remarks
   * 对应 PlayCanvas 的 prepareSortParameters() 方法
   */
  private prepareSortParameters(worldState: GSplatWorldState): SortParameters {
    const intervals = worldState.splats.map((splat) =>
      splat.intervals.length > 0 ? [...splat.intervals] : [0, splat.activeSplats]
    )
    const pixelOffsets = worldState.splats.map((splat, splatIndex) => {
      const offsets: number[] = []
      let offset = splat.lineStart * worldState.textureSize
      const activeIntervals = intervals[splatIndex]
      for (let index = 0; index < activeIntervals.length; index += 2) {
        offsets.push(offset)
        offset += activeIntervals[index + 1] - activeIntervals[index]
      }
      return offsets
    })
    return {
      command: 'intervals',
      textureSize: worldState.textureSize,
      totalActiveSplats: worldState.totalActiveSplats,
      version: worldState.version,
      ids: worldState.splats.map((splat) => splat.resource.id),
      pixelOffsets,
      intervals,
    }
  }

  // ==================== 调试辅助 ====================

  /**
   * 获取调试颜色（用于可视化 LOD 或颜色更新）
   *
   * @returns 颜色数组（每个 LOD 级别一个颜色）或 undefined（正常渲染）
   *
   * @remarks
   * 对应 PlayCanvas 的 getDebugColors() 方法
   */
  private getDebugColors(): number[][] | undefined {
    if (this.colorizeColorUpdate) {
      // 随机颜色（用于可视化颜色更新）
      const r = Math.random()
      const g = Math.random()
      const b = Math.random()
      return Array(8)
        .fill(null)
        .map(() => [r, g, b])
    } else if (this.colorizeLod) {
      // LOD 级别颜色
      return [
        [1, 0, 0], // red
        [0, 1, 0], // green
        [0, 0, 1], // blue
        [1, 1, 0], // yellow
        [1, 0, 1], // magenta
        [0, 1, 1], // cyan
        [1, 0.5, 0], // orange
        [0.5, 0, 1], // purple
      ]
    }
    return undefined
  }

  // ==================== 主更新循环 ====================

  /**
   * 主更新循环（每帧调用）
   *
   * @returns 渲染的 splat 数量
   *
   * @remarks
   * 对应 PlayCanvas 的 update() 方法
   * 核心流程：
   * 1. 应用已完成的排序结果
   * 2. 每10帧执行完整更新（检测资源加载、移动）
   * 3. 检测相机移动触发 LOD 评估
   * 4. 创建新的世界状态（如果需要）
   * 5. 检测相机移动触发排序
   * 6. 颜色累积器更新（球谐函数增量更新）
   * 7. 批量渲染到 WorkBuffer
   * 8. 冷却计数器更新
   */
  update(cooldownFrameId?: number, cooldownFrameSource?: object): number {
    const resolvedCooldownFrameId =
      typeof cooldownFrameId === 'number' && Number.isFinite(cooldownFrameId)
        ? Math.floor(cooldownFrameId)
        : undefined
    const resolvedCooldownFrameSource =
      resolvedCooldownFrameId === undefined ? undefined : cooldownFrameSource

    // worker 重建后 centers 已清空，置脏标记触发 updateWorldState 全量重发
    if (this.sorter.consumeWorkerRecovered()) {
      this.layerPlacementsDirty = true
      this.sortNeeded = true
    }

    if (this.sorter.consumeSortRetryNeeded()) {
      this.sortNeeded = true
    }

    if (this.testCameraMovedForSort()) {
      this.markInteractiveSortActivity()
      this.sortNeeded = true
    }

    // 1. 应用已完成的排序结果
    this.sorter.applyPendingSorted()
    this.tryApplyPendingOrder()

    // 2. 每10帧执行完整更新（如果 sorter 能跟上）。资源已经就绪时优先
    // 推进 LOD 提交，避免按需渲染停在最后一个过渡状态。
    let fullUpdate = false
    if (this.clipLodDirty || this.canAdvanceLodPresentationWork()) {
      this.framesTillFullUpdate = 0
    }
    this.framesTillFullUpdate--
    if (this.framesTillFullUpdate <= 0) {
      this.framesTillFullUpdate = 10

      // 检查 sorter 是否能跟上（飞行中的任务少于3个）
      if (this.sorter.jobsInFlight < 3) {
        fullUpdate = true
      }
    }

    // 3. 检测是否有新的 Octree 实例（触发立即 LOD 评估）
    const hasNewInstances = this.hasNewOctreeInstances && this.sorter.jobsInFlight < 3
    if (hasNewInstances) {
      this.hasNewOctreeInstances = false
    }

    let anyInstanceNeedsLodUpdate = false
    let anyOctreeMoved = false
    let cameraMovedOrRotatedForLod = false

    if (fullUpdate) {
      // 4. 处理 Octree 实例更新（资源加载完成、needsLodUpdate 检测）
      for (const [, inst] of this.octreeInstances) {
        const isDirty = inst.update()
        this.layerPlacementsDirty ||= isDirty

        const instNeeds = inst.consumeNeedsLodUpdate()
        anyInstanceNeedsLodUpdate ||= instNeeds
      }

      // 5. 检测 Octree 实例移动
      const threshold = this.params.lodUpdateDistance
      for (const [, inst] of this.octreeInstances) {
        const moved = inst.testMoved(threshold)
        anyOctreeMoved ||= moved
      }

      // 6. 检测相机移动（LOD 评估）
      cameraMovedOrRotatedForLod = this.testCameraMovedForLod()
    }

    // 7. 检测相机移动（排序）
    if (this.testCameraMovedForSort()) {
      this.markInteractiveSortActivity()
      this.sortNeeded = true
    }

    // 8. 参数脏标记检测
    if (this.params.dirty) {
      this.layerPlacementsDirty = true
    }

    // 9. 触发 LOD 评估（当相机移动、Octree 移动、参数变化、资源完成时）
    if (
      cameraMovedOrRotatedForLod ||
      anyOctreeMoved ||
      this.params.dirty ||
      anyInstanceNeedsLodUpdate ||
      hasNewInstances
    ) {
      // 更新 Octree 实例的上一帧位置
      for (const [, inst] of this.octreeInstances) {
        inst.updateMoved()
      }

      // 更新上一帧相机位置
      this.cameraNode.updateMatrixWorld(true)
      this._tempCameraPos.setFromMatrixPosition(this.cameraNode.matrixWorld)
      this._tempCameraRot.setFromRotationMatrix(this.cameraNode.matrixWorld)
      this._tempCameraDir.set(0, 0, -1).applyQuaternion(this._tempCameraRot)
      this.lastLodCameraPos.copy(this._tempCameraPos)
      this.lastLodCameraFwd.copy(this._tempCameraDir)
      const camera = this.cameraNode as THREE.PerspectiveCamera
      if (camera.isPerspectiveCamera) {
        this.lastLodCameraFov = camera.fov
        this.lastLodCameraAspect = camera.aspect
      }

      // 更新所有 Octree 实例的 LOD
      const budget = Math.max(0, Math.floor(this.params.maxCachePoints))
      if (budget > 0) {
        this.enforceBudget(budget)
      } else {
        this.budgetScale = 1.0
        for (const [, inst] of this.octreeInstances) {
          inst.updateLod(this.cameraNode, this.params)
        }
      }
      this.clipLodDirty = false
    }

    // 10. 创建新的世界状态（如果需要 + 节流）
    // 递减冷却计数器
    if (this.layerPlacementsDirty) {
      this.worldStateUpdateCooldown--
    }

    // 检查是否应该更新 WorldState
    const shouldUpdateWorldState =
      this.layerPlacementsDirty &&
      (this.worldStateUpdateCooldown <= 0 || this.worldStates.size === 0)

    if (shouldUpdateWorldState) {
      this.updateWorldState()
      // 重置冷却计数器
      const interval = this.params.worldStateUpdateInterval
      this.worldStateUpdateCooldown = interval > 0 ? interval : 0
    }

    // 11. 触发排序（如果需要）
    const lastState = this.worldStates.get(this.lastWorldStateVersion)
    if (lastState) {
      // 设置排序参数（仅首次）
      if (!lastState.sortParametersSet) {
        lastState.sortParametersSet = true
        const payload = this.prepareSortParameters(lastState)
        this.sorter.setSortParameters(payload)
      }

      // 触发排序
      if (this.sortNeeded) {
        // 更新 Sorter 的排序模式
        this.sorter.setRadialSort(this.radialSort)

        this.handleSortRequestResult(this.sort(lastState))
      }
    }

    // 12. 颜色累积器更新（使用已排序的世界状态）
    const sortedState = this.worldStates.get(this.sortedVersion)
    if (sortedState) {
      // 计算相机移动增量
      const { rotationDelta, translationDelta } = this.calculateColorCameraDeltas()

      // 检查每个 Splat 是否需要更新
      sortedState.splats.forEach((splat) => {
        // WorkBuffer 始终保存资产根节点局部坐标，根节点变换只影响最终绘制和排序。
        if (splat.update()) {
          if (splat.hasSphericalHarmonics) {
            this._tempSplatsNeedingColorUpdate.push(splat)
          }
          if (splat.hasSphericalHarmonics) {
            splat.resetColorAccumulators(this.colorUpdateAngle, this.colorUpdateDistance)
          }
          this.sortNeeded = true
        } else if (splat.hasSphericalHarmonics) {
          // 累积相机移动
          splat.colorAccumulatedRotation += rotationDelta
          splat.colorAccumulatedTranslation += translationDelta

          // 应用 LOD 缩放到阈值
          const lodIndex = splat.lodIndex ?? 0
          const distThreshold =
            this.colorUpdateDistance * this.colorUpdateDistanceLodScale ** lodIndex
          const angleThreshold = this.colorUpdateAngle * this.colorUpdateAngleLodScale ** lodIndex

          // 超过阈值：触发颜色更新
          if (
            splat.colorAccumulatedRotation >= angleThreshold ||
            splat.colorAccumulatedTranslation >= distThreshold
          ) {
            this._tempSplatsNeedingColorUpdate.push(splat)
            splat.resetColorAccumulators(angleThreshold, distThreshold)
          }
        }
      })

      // 13. 批量渲染更新
      const forceWorkBufferRender =
        sortedState.splats.length > 0 && this.shouldForceWorkBufferRender(sortedState.splats)
      if (forceWorkBufferRender) {
        this.workBuffer.render(sortedState.splats, this.cameraNode, this.getDebugColors())
        if (this._scalarFilterMode !== GSplatScalarMode.None) {
          this.workBuffer.markScalarDirty()
        }
        this._tempSplatsNeedingColorUpdate.length = 0
        this._workBufferDirty = false
        this.consumeWorkBufferUpdateOnce(sortedState.splats)
      }

      if (!forceWorkBufferRender && this._tempSplatsNeedingColorUpdate.length > 0) {
        this.workBuffer.renderColor(
          this._tempSplatsNeedingColorUpdate,
          this.cameraNode,
          this.getDebugColors()
        )
        if (this._scalarFilterMode !== GSplatScalarMode.None) {
          this.workBuffer.markScalarDirty()
        }
        this._tempSplatsNeedingColorUpdate.length = 0
      }

      // 14. 更新颜色相机追踪
      this.updateColorCameraTracking()

      // 15. scalar pass（仅在 dirty 时执行）
      if (this._scalarFilterMode !== GSplatScalarMode.None) {
        this.workBuffer.renderScalar(sortedState.splats, this.cameraNode)
        this.updateScalarValuesCache()
      }
    }

    // 16. 返回 active splat 数量，不包含 WorkBuffer 行 padding
    return this.finishUpdateFrame(resolvedCooldownFrameId, resolvedCooldownFrameSource)
  }

  private finishUpdateFrame(cooldownFrameId?: number, cooldownFrameSource?: object): number {
    const visibleSplatCount = this.visibleSplatCount
    if (!this.firstVisibleSplatsLogged && visibleSplatCount > 0) {
      this.firstVisibleSplatsLogged = true
      log.info(`[GSplatManager] first visible splats, visibleSplats=${visibleSplatCount}`)
    }
    this.tickOctreeCooldowns(cooldownFrameId, cooldownFrameSource)
    this.params.frameEnd()
    return visibleSplatCount
  }

  private tickOctreeCooldowns(frameId?: number, frameSource?: object): void {
    if (
      this.octreeInstances.size === 0 &&
      (this.octreeInstancesToDestroy?.length ?? 0) === 0 &&
      (this.retiredOctrees?.size ?? 0) === 0
    ) {
      return
    }

    const cooldownTicks = this.params.cooldownTicks
    for (const instance of this.octreeInstances.values()) {
      const octree = instance.octree
      if (this._tempOctreesTicked.has(octree)) {
        continue
      }
      this._tempOctreesTicked.add(octree)
      octree.updateCooldownTick(cooldownTicks, frameId, frameSource)
    }
    for (const retirement of this.octreeInstancesToDestroy ?? []) {
      const octree = retirement.instance.octree
      if (this._tempOctreesTicked.has(octree)) {
        continue
      }
      this._tempOctreesTicked.add(octree)
      octree.updateCooldownTick(cooldownTicks, frameId, frameSource)
    }
    for (const octree of this.retiredOctrees ?? []) {
      if (this._tempOctreesTicked.has(octree)) {
        continue
      }
      this._tempOctreesTicked.add(octree)
      octree.updateCooldownTick(cooldownTicks, frameId, frameSource)
    }
    this._tempOctreesTicked.clear()

    for (const octree of this.retiredOctrees ?? []) {
      if (!octree.hasPendingCooldowns) {
        this.retiredOctrees.delete(octree)
      }
    }
  }

  // ==================== 公共接口 ====================

  /**
   * 获取颜色纹理（用于材质绑定）
   */
  get colorTexture(): THREE.DataTexture {
    return this.workBuffer.colorTexture
  }

  /**
   * 获取几何纹理0（用于材质绑定）
   */
  get splatTexture0(): THREE.DataTexture {
    return this.workBuffer.splatTexture0
  }

  /**
   * 获取几何纹理1（用于材质绑定）
   */
  get splatTexture1(): THREE.DataTexture {
    return this.workBuffer.splatTexture1
  }

  /**
   * 获取排序索引纹理（用于材质绑定）
   */
  get orderTexture(): THREE.DataTexture {
    return this.workBuffer.orderTexture
  }

  /**
   * 获取纹理尺寸
   */
  get textureSize(): number {
    return this.workBuffer.textureSize
  }

  get isWorkBufferRenderable(): boolean {
    return this.workBuffer.renderable
  }

  /**
   * 获取当前可见 active splat 数量，不包含 WorkBuffer 行 padding。
   */
  get visibleSplatCount(): number {
    // 必须使用已排序的版本，而非最新创建的版本
    // lastWorldStateVersion 可能领先 sortedVersion，此时 WorkBuffer 尚未更新
    const worldState = this.worldStates.get(this.sortedVersion)
    return worldState?.sortedVisibleSplatCount ?? 0
  }

  /**
   * 获取最近一次排序可见集 compact 统计。
   */
  get sortVisibilityCompactStats() {
    return this.sorter.lastVisibilityCompactStats
  }

  get budgetRuntimeDiagnostics(): GSplatBudgetRuntimeDiagnostics {
    return {
      ...this._budgetRuntimeDiagnostics,
      budgetScale: this.budgetScale,
    }
  }

  get clipLodDiagnostics(): GSplatClipLodDiagnostics {
    const result: GSplatClipLodDiagnostics = {
      classifiedNodes: 0,
      polygonCulledNodes: 0,
      boxCulledNodes: 0,
      mixedCulledNodes: 0,
      intersectingNodes: 0,
      excludedSplats: 0,
    }

    for (const instance of this.octreeInstances.values()) {
      const diagnostics = instance.clipLodDiagnostics
      result.classifiedNodes += diagnostics.classifiedNodes
      result.polygonCulledNodes += diagnostics.polygonCulledNodes
      result.boxCulledNodes += diagnostics.boxCulledNodes
      result.mixedCulledNodes += diagnostics.mixedCulledNodes
      result.intersectingNodes += diagnostics.intersectingNodes
      result.excludedSplats += diagnostics.excludedSplats
    }
    return result
  }

  get orderUploadDiagnostics(): GSplatOrderUploadDiagnostics {
    const now = this.orderUploadNowMs()
    const pendingAgeMs = this.pendingOrder
      ? Math.max(0, now - this.pendingOrder.receivedAtMs)
      : null
    const sorterDiagnostics = this.sorter?.diagnostics
    const pendingWorkerToApplyLatencyMs = this.pendingOrder
      ? Math.max(0, now - this.pendingOrder.workerCompletedAtMs)
      : null
    const stagingInfo = this.workBuffer.getOrderStagingInfo?.() ?? null

    return {
      ...this._orderUploadDiagnostics,
      observedUploadAvailable:
        this._orderUploadDiagnostics.observedUploadAvailable ||
        this.orderUploadObservedProbeInstalled,
      pendingAgeMs,
      pendingSortGeneration: this.pendingOrder?.sortGeneration ?? null,
      pendingWorldStateVersion: this.pendingOrder?.version ?? null,
      pendingOrderSkippedFrameCount: this.pendingOrder?.skippedFrameCount ?? null,
      stagingSortGeneration: this.stagingOrder?.sortGeneration ?? stagingInfo?.generation ?? null,
      stagingTextureSize: stagingInfo?.textureSize ?? null,
      stagingVisibleCount: stagingInfo?.visibleCount ?? null,
      stagingTotalBytes: stagingInfo?.totalBytes ?? this._orderUploadDiagnostics.stagingTotalBytes,
      stagingSubmittedBytes:
        stagingInfo?.submittedBytes ?? this._orderUploadDiagnostics.stagingSubmittedBytes,
      stagingRemainingBytes:
        stagingInfo?.remainingBytes ?? this._orderUploadDiagnostics.stagingRemainingBytes,
      stagingSubmittedChunks:
        stagingInfo?.submittedChunks ?? this._orderUploadDiagnostics.stagingSubmittedChunks,
      stagingRemainingChunks:
        stagingInfo?.remainingChunks ?? this._orderUploadDiagnostics.stagingRemainingChunks,
      pendingRequestAgeMs: sorterDiagnostics?.pendingRequestAgeMs ?? null,
      pendingRequestToSubmitLatencyMs: this.pendingOrder?.requestToSubmitLatencyMs ?? null,
      pendingWorkerToApplyLatencyMs,
      coalescedResultCount:
        this._orderUploadDiagnostics.coalescedResultCount +
        (this.sorter?.coalescedResultCount ?? 0),
      staleResultCount:
        this._orderUploadDiagnostics.staleResultCount + (this.sorter?.staleResultCount ?? 0),
      submittedRequestCount: sorterDiagnostics?.submittedRequestCount ?? 0,
      queuedRequestCount: sorterDiagnostics?.queuedRequestCount ?? 0,
      replayedRequestCount: sorterDiagnostics?.replayedRequestCount ?? 0,
      overwrittenRequestCount: sorterDiagnostics?.overwrittenRequestCount ?? 0,
      rejectedRequestCount: sorterDiagnostics?.rejectedRequestCount ?? 0,
      workerErrorCount: sorterDiagnostics?.workerErrorCount ?? 0,
      lastRequestToSubmitLatencyMs:
        sorterDiagnostics?.lastRequestToSubmitLatencyMs ??
        this._orderUploadDiagnostics.lastRequestToSubmitLatencyMs,
      orderBufferAllocCount: sorterDiagnostics?.orderBufferAllocCount ?? 0,
      orderBufferReuseCount: sorterDiagnostics?.orderBufferReuseCount ?? 0,
      orderBufferReleaseCount: sorterDiagnostics?.orderBufferReleaseCount ?? 0,
      pendingOrderBufferLeaseCount:
        (sorterDiagnostics?.pendingOrderBufferLeaseCount ?? 0) +
        (this.pendingOrder?.releaseOrderData ? 1 : 0) +
        (this.stagingOrder?.releaseOrderData ? 1 : 0),
      adaptiveReplayDeferralCount: sorterDiagnostics?.adaptiveReplayDeferralCount ?? 0,
      adaptiveReplayTimerFireCount: sorterDiagnostics?.adaptiveReplayTimerFireCount ?? 0,
      adaptiveSortIntervalMs: sorterDiagnostics?.adaptiveSortIntervalMs ?? 0,
      workerSortTimeEmaMs: sorterDiagnostics?.workerSortTimeEmaMs ?? 0,
      pendingReplayDelayMs: sorterDiagnostics?.pendingReplayDelayMs ?? null,
    }
  }

  /**
   * unified LOD 不共享全局 SH 结果纹理；颜色由逐 placement WorkBuffer pass 计算。
   */
  get shResultTexture(): THREE.Texture | null {
    return null
  }

  /** unified LOD 的全局高质量 SH 预计算已禁用，避免多实例矩阵互相污染。 */
  get isHighQualitySHActive(): boolean {
    return false
  }

  // ==================== 标量过滤公共接口 ====================

  /**
   * 获取标量纹理（用于材质绑定）
   */
  get scalarTexture(): THREE.DataTexture | null {
    return this.workBuffer.scalarTexture
  }

  /**
   * 获取标量纹理版本号
   */
  get scalarTextureVersion(): number {
    return this.workBuffer.scalarVersion
  }

  /**
   * 获取当前 pick buffer 状态版本。
   */
  get pickStateVersion(): number {
    return this._pickStateVersion
  }

  /**
   * 设置标量过滤。mode 变化时调用 workBuffer.setScalarMode()，阈值变化只更新配置。
   *
   * @param options - 过滤参数，含启用开关、标量模式与阈值区间
   */
  setScalarFilter(options: {
    enabled: boolean
    mode: GSplatScalarMode
    min: number
    max: number
  }): void {
    const nextMode = options.enabled ? options.mode : GSplatScalarMode.None
    const modeChanged = this._scalarFilterMode !== nextMode
    const enabledChanged = this._scalarFilterEnabled !== options.enabled
    const rangeChanged =
      this._scalarFilterMin !== options.min || this._scalarFilterMax !== options.max

    this._scalarFilterEnabled = options.enabled
    this._scalarFilterMode = nextMode
    this._scalarFilterMin = options.min
    this._scalarFilterMax = options.max

    if (modeChanged || enabledChanged) {
      this.workBuffer.setScalarMode(nextMode)
    }

    if (modeChanged) {
      this.resetScalarValuesCache()
    }

    if (options.enabled && (modeChanged || enabledChanged || rangeChanged)) {
      this.updateScalarValuesCache()
    }

    if (options.enabled && (modeChanged || enabledChanged || rangeChanged)) {
      this.logScalarFilterSnapshot('setScalarFilter')
    }

    if (modeChanged || enabledChanged || rangeChanged) {
      this.bumpPickStateVersion()
    }
  }

  /**
   * 清除标量过滤（保留 scalarTexture 缓存）
   */
  clearScalarFilter(): void {
    const hadScalarFilter =
      this._scalarFilterEnabled || this._scalarFilterMode !== GSplatScalarMode.None
    this._scalarFilterEnabled = false
    this._scalarFilterMode = GSplatScalarMode.None
    this.workBuffer.setScalarMode(GSplatScalarMode.None)
    if (hadScalarFilter) {
      this.bumpPickStateVersion()
    }
  }

  /**
   * 获取标量过滤配置（供 UnifiedLODMesh 使用）。
   *
   * @returns 当前过滤配置；未启用时返回 null
   */
  getScalarFilterConfig(): { enabled: boolean; min: number; max: number } | null {
    if (!this.isScalarFilterActive) return null
    return {
      enabled: true,
      min: this._scalarFilterMin,
      max: this._scalarFilterMax,
    }
  }

  /** 当前是否有生效的标量过滤，同时要求 WorkBuffer 支持过滤。 */
  get isScalarFilterActive(): boolean {
    return (
      this._scalarFilterEnabled &&
      this._scalarFilterMode !== GSplatScalarMode.None &&
      this.workBuffer.supportsScalarFiltering
    )
  }

  /**
   * 判断指定 splat 是否通过标量过滤。
   *
   * @param splatInfo - 所在资源的 SplatInfo
   * @param splatIndex - 资源内原始 splat 索引
   * @returns 通过过滤返回 true；未启用过滤时恒为 true
   */
  passesScalarFilter(splatInfo: GSplatInfo, splatIndex: number): boolean {
    if (splatInfo.placement.isEnvironment || !this.isScalarFilterActive) {
      return true
    }

    const readback = this.readScalarValues(this._scalarFilterMode)
    if (!readback) {
      return false
    }

    const activeIndex = splatInfo.getActiveSplatIndex(splatIndex)
    if (activeIndex < 0) {
      return false
    }

    const textureSize = this.workBuffer.textureSize
    const globalIndex = splatInfo.lineStart * textureSize + activeIndex
    if (globalIndex < 0 || globalIndex >= readback.values.length) {
      return false
    }

    const scalar = readback.values[globalIndex]
    return scalar >= this._scalarFilterMin && scalar <= this._scalarFilterMax
  }

  /**
   * 计算当前 manager 下非环境高斯的指定标量属性直方图。
   *
   * @param options - 直方图配置，含 mode 和可选 binCount
   * @returns 直方图结果；模式为 None 或当前没有可用主体 splat 时返回 null
   */
  getScalarHistogram(options: GSplatScalarHistogramOptions): ScalarHistogramResult | null {
    if (options.mode === GSplatScalarMode.None) {
      return null
    }

    const sortedState = this.worldStates.get(this.sortedVersion)
    if (!sortedState || sortedState.splats.length === 0) {
      return null
    }

    const histogramSplats = sortedState.splats.filter(
      (splatInfo) => !splatInfo.placement.isEnvironment && splatInfo.activeSplats > 0
    )
    if (histogramSplats.length === 0) {
      return null
    }

    const readback = this.readScalarValues(options.mode)
    if (!readback) {
      return null
    }

    const textureSize = this.workBuffer.textureSize
    const ranges = histogramSplats.map((splatInfo) => ({
      baseIndex: splatInfo.lineStart * textureSize,
      count: splatInfo.activeSplats,
    }))

    const result = computeScalarHistogram({
      values: readback.values,
      ranges,
      textureVersion: readback.textureVersion,
      binCount: options.binCount,
      scale: options.scale,
      percentileRange: options.percentileRange,
    })

    if (result && isScalarFilterDebugEnabled()) {
      log.info(
        '[GSplatManager][ScalarFilter] histogram mode=' +
          getScalarModeLabel(options.mode) +
          ', scale=' +
          (options.scale ?? 'linear') +
          ', percentileMin=' +
          clampPercentile(options.percentileRange?.lower, 0) +
          ', percentileMax=' +
          clampPercentile(options.percentileRange?.upper, 100) +
          ', min=' +
          result.min +
          ', max=' +
          result.max +
          ', displayMin=' +
          result.displayMin +
          ', displayMax=' +
          result.displayMax +
          ', sampleCount=' +
          result.sampleCount +
          ', clippedSampleCount=' +
          result.clippedSampleCount +
          ', textureVersion=' +
          result.textureVersion
      )
    }

    return result
  }

  private updateScalarValuesCache(): void {
    if (this.isScalarFilterActive) {
      this.readScalarValues(this._scalarFilterMode)
    }
  }

  private logScalarFilterSnapshot(reason: string): void {
    if (!isScalarFilterDebugEnabled()) {
      return
    }

    if (!this.isScalarFilterActive) {
      log.info(
        '[GSplatManager][ScalarFilter] inactive reason=' +
          reason +
          ', enabled=' +
          this._scalarFilterEnabled +
          ', mode=' +
          getScalarModeLabel(this._scalarFilterMode) +
          ', supportsScalarFiltering=' +
          this.workBuffer.supportsScalarFiltering
      )
      return
    }

    const sortedState = this.worldStates.get(this.sortedVersion)
    if (!sortedState || sortedState.splats.length === 0) {
      log.info(
        '[GSplatManager][ScalarFilter] no sorted splats reason=' +
          reason +
          ', sortedVersion=' +
          this.sortedVersion +
          ', mode=' +
          getScalarModeLabel(this._scalarFilterMode) +
          ', min=' +
          this._scalarFilterMin +
          ', max=' +
          this._scalarFilterMax
      )
      return
    }

    const readback = this.readScalarValues(this._scalarFilterMode)
    if (!readback) {
      log.info(
        '[GSplatManager][ScalarFilter] readback unavailable reason=' +
          reason +
          ', sortedVersion=' +
          this.sortedVersion +
          ', mode=' +
          getScalarModeLabel(this._scalarFilterMode) +
          ', min=' +
          this._scalarFilterMin +
          ', max=' +
          this._scalarFilterMax
      )
      return
    }

    const textureSize = this.workBuffer.textureSize
    let sampleCount = 0
    let finiteCount = 0
    let passCount = 0
    let belowMinCount = 0
    let aboveMaxCount = 0
    let exactMinCount = 0
    let exactMaxCount = 0
    let actualMin = Number.POSITIVE_INFINITY
    let actualMax = Number.NEGATIVE_INFINITY

    for (const splatInfo of sortedState.splats) {
      const baseIndex = splatInfo.lineStart * textureSize
      for (let activeIndex = 0; activeIndex < splatInfo.activeSplats; activeIndex++) {
        sampleCount++
        const scalar = readback.values[baseIndex + activeIndex]
        if (!Number.isFinite(scalar)) {
          continue
        }

        finiteCount++
        actualMin = Math.min(actualMin, scalar)
        actualMax = Math.max(actualMax, scalar)

        if (scalar < this._scalarFilterMin) {
          belowMinCount++
          continue
        }

        if (scalar > this._scalarFilterMax) {
          aboveMaxCount++
          continue
        }

        if (scalar === this._scalarFilterMin) {
          exactMinCount++
        }

        if (scalar === this._scalarFilterMax) {
          exactMaxCount++
        }

        passCount++
      }
    }

    log.info(
      '[GSplatManager][ScalarFilter] snapshot reason=' +
        reason +
        ', mode=' +
        getScalarModeLabel(this._scalarFilterMode) +
        ', min=' +
        this._scalarFilterMin +
        ', max=' +
        this._scalarFilterMax +
        ', width=' +
        (this._scalarFilterMax - this._scalarFilterMin) +
        ', sampleCount=' +
        sampleCount +
        ', finiteCount=' +
        finiteCount +
        ', passCount=' +
        passCount +
        ', belowMinCount=' +
        belowMinCount +
        ', aboveMaxCount=' +
        aboveMaxCount +
        ', exactMinCount=' +
        exactMinCount +
        ', exactMaxCount=' +
        exactMaxCount +
        ', actualMin=' +
        (finiteCount > 0 ? actualMin : null) +
        ', actualMax=' +
        (finiteCount > 0 ? actualMax : null) +
        ', sortedVersion=' +
        this.sortedVersion +
        ', textureVersion=' +
        readback.textureVersion +
        ', textureSize=' +
        textureSize
    )
  }

  private readScalarValues(
    mode: GSplatScalarMode
  ): { values: Float32Array; textureVersion: number } | null {
    if (mode === GSplatScalarMode.None) {
      return null
    }

    const sortedState = this.worldStates.get(this.sortedVersion)
    if (!sortedState || sortedState.splats.length === 0) {
      return null
    }

    const originalMode = this.workBuffer.scalarMode
    const needsTemporaryMode = originalMode !== mode

    if (needsTemporaryMode) {
      this.workBuffer.setScalarMode(mode)
      this.workBuffer.markScalarDirty()
    }

    this.workBuffer.renderScalar(sortedState.splats, this.cameraNode)
    const scalarVersion = this.workBuffer.scalarVersion

    if (scalarVersion <= 0) {
      if (needsTemporaryMode) {
        this.workBuffer.setScalarMode(originalMode)
      }
      return null
    }

    const canUseSharedCache = !needsTemporaryMode || originalMode === GSplatScalarMode.None

    if (
      canUseSharedCache &&
      this._scalarValues &&
      this._scalarValuesVersion === scalarVersion &&
      this._scalarValuesMode === mode
    ) {
      if (needsTemporaryMode) {
        this.workBuffer.setScalarMode(originalMode)
      }
      return {
        values: this._scalarValues,
        textureVersion: scalarVersion,
      }
    }

    const scalarRenderTarget = this.workBuffer.scalarRenderTarget
    if (!scalarRenderTarget) {
      if (needsTemporaryMode) {
        this.workBuffer.setScalarMode(originalMode)
        if (originalMode !== GSplatScalarMode.None) {
          this.workBuffer.renderScalar(sortedState.splats, this.cameraNode)
        }
      }
      return null
    }

    const size = this.workBuffer.textureSize
    const buffer = new Float32Array(size * size)

    try {
      this.renderer.readRenderTargetPixels(scalarRenderTarget, 0, 0, size, size, buffer)

      if (canUseSharedCache) {
        this._scalarValues = buffer
        this._scalarValuesVersion = scalarVersion
        this._scalarValuesMode = mode
      }

      if (needsTemporaryMode) {
        this.workBuffer.setScalarMode(originalMode)
        if (originalMode !== GSplatScalarMode.None) {
          this.workBuffer.renderScalar(sortedState.splats, this.cameraNode)
        }
      }

      return {
        values: buffer,
        textureVersion: scalarVersion,
      }
    } catch (error) {
      if (needsTemporaryMode) {
        this.workBuffer.setScalarMode(originalMode)
        if (originalMode !== GSplatScalarMode.None) {
          this.workBuffer.renderScalar(sortedState.splats, this.cameraNode)
        }
      }
      this.resetScalarValuesCache()
      log.warn(
        '[GSplatManager] Failed to read back scalarTexture: ' +
          (error instanceof Error ? error.message : String(error))
      )
      return null
    }
  }

  private resetScalarValuesCache(): void {
    this._scalarValues = null
    this._scalarValuesVersion = -1
    this._scalarValuesMode = GSplatScalarMode.None
  }

  private bumpPickStateVersion(): void {
    this._pickStateVersion++
  }

  /**
   * 获取正在加载的文件数量（用于加载进度显示）
   */
  get loadingCount(): number {
    let count = 0
    for (const [, inst] of this.octreeInstances) {
      count += inst.pendingLoadCount
    }
    return count
  }

  /**
   * 检测是否所有资源已加载完成
   */
  get isReady(): boolean {
    return this.sortedVersion === this.lastWorldStateVersion
  }

  /**
   * 当前相机对应的 LOD 是否仍在等待进入最终可显示帧。
   *
   * `currentLod` 会早于 Placement 协调、WorldState 创建、排序 Worker 和
   * orderTexture 应用完成，因此不能只用节点 LOD 一致性判断视觉稳定。
   * 同一 WorldState 上因相机运动产生的后续排序不影响 LOD 是否已经呈现，
   * 否则持续相机动画会让初始加载永远无法完成。
   * 该状态包含异步网络等待，只用于完成度诊断，不能直接驱动逐帧渲染；
   * 资源和排序完成事件会分别唤醒按需渲染。
   */
  get lodPresentationPending(): boolean {
    const hasPlacements = this.layerPlacements.length > 0 || this.octreeInstances.size > 0
    if (!hasPlacements) {
      return false
    }

    for (const instance of this.octreeInstances.values()) {
      if (instance.lodPresentationPending) {
        return true
      }
    }

    return (
      this.hasNewOctreeInstances ||
      this.params.dirty ||
      this.layerPlacementsDirty ||
      this.worldStates.size === 0 ||
      (this.sortedVersion !== this.lastWorldStateVersion && !this.sorter.isWorkerUnavailable)
    )
  }

  // ==================== 光线拾取 ====================

  /**
   * 获取当前所有活跃的 GSplatInfo 对象
   * 用于外部直接调用 GSplatPicker
   *
   * @returns GSplatInfo 数组，如果没有已排序的状态则返回空数组
   *
   * @remarks
   * 返回的是最新已排序版本（sortedVersion）对应的 splats
   * 这保证了返回的 splats 已经完成排序并可以安全访问
   */
  getActiveSplatInfos(): GSplatInfo[] {
    const sortedState = this.worldStates.get(this.sortedVersion)
    if (!sortedState) {
      return []
    }
    return sortedState.splats
  }

  /**
   * 使用光线拾取场景中的 splats
   * 会自动检测所有当前活跃的 GSplatInfo 对象
   *
   * @param ray - 世界空间光线
   * @param options - 拾取选项
   * @returns 按距离排序的拾取结果数组
   *
   * @remarks
   * - 仅检测已完成排序的 splats（sortedVersion）
   * - 如果没有活跃的 splats，返回空数组
   * - 结果按距离从近到远排序
   * - 可通过 options.maxResults 限制返回数量
   *
   * @example
   * ```typescript
   * const ray = new THREE.Ray(origin, direction)
   * const results = manager.pickSplats(ray, { maxResults: 10 })
   * results.forEach(result => {
   *   console.log(`拾取到 splat ${result.splatIndex}，距离 ${result.distance}`)
   * })
   * ```
   */
  pickSplats(ray: THREE.Ray, options?: GSplatPickOptions): GSplatPickResult[] {
    const activeSplatInfos = this.getActiveSplatInfos()

    if (activeSplatInfos.length === 0) {
      return []
    }

    const requestedMaxResults = options?.maxResults ?? DEFAULT_MAX_PICK_RESULTS
    const pickOptions = this.getPickOptionsWithScalarFilter(options, activeSplatInfos)
    const results = GSplatPicker.pickMultiple(ray, activeSplatInfos, pickOptions)
    return this.filterPickResults(results, requestedMaxResults)
  }

  /**
   * 从屏幕坐标拾取 splats
   *
   * @param screenX - 屏幕 X 坐标（像素，左上角为原点）
   * @param screenY - 屏幕 Y 坐标（像素，左上角为原点）
   * @param screenWidth - 屏幕宽度（像素）
   * @param screenHeight - 屏幕高度（像素）
   * @param camera - Three.js 相机
   * @param options - 拾取选项
   * @returns 按距离排序的拾取结果数组
   *
   * @remarks
   * - 自动将屏幕坐标转换为世界空间光线
   * - 屏幕坐标原点为左上角
   * - 仅检测已完成排序的 splats（sortedVersion）
   * - 如果没有活跃的 splats，返回空数组
   *
   * @example
   * ```typescript
   * // 监听鼠标点击
   * canvas.addEventListener('click', (event) => {
   *   const results = manager.pickSplatsFromScreen(
   *     event.clientX,
   *     event.clientY,
   *     canvas.width,
   *     canvas.height,
   *     camera,
   *     { maxResults: 1 }
   *   )
   *   if (results.length > 0) {
   *     console.log('点击到了 splat:', results[0])
   *   }
   * })
   * ```
   */
  pickSplatsFromScreen(
    screenX: number,
    screenY: number,
    screenWidth: number,
    screenHeight: number,
    camera: THREE.Camera,
    options?: GSplatPickOptions
  ): GSplatPickResult[] {
    const activeSplatInfos = this.getActiveSplatInfos()

    if (activeSplatInfos.length === 0) {
      return []
    }

    const requestedMaxResults = options?.maxResults ?? DEFAULT_MAX_PICK_RESULTS
    const pickOptions = this.getPickOptionsWithScalarFilter(options, activeSplatInfos)
    const results = GSplatPicker.pickFromScreen(
      screenX,
      screenY,
      screenWidth,
      screenHeight,
      camera,
      activeSplatInfos,
      pickOptions
    )
    return this.filterPickResults(results, requestedMaxResults)
  }

  private getPickOptionsWithScalarFilter(
    options: GSplatPickOptions | undefined,
    activeSplatInfos: GSplatInfo[]
  ): GSplatPickOptions | undefined {
    if (!this.isScalarFilterActive) {
      return options
    }

    const maxResults = activeSplatInfos.reduce((sum, splatInfo) => sum + splatInfo.numSplats, 0)
    return {
      ...options,
      maxResults,
    }
  }

  private filterPickResults(results: GSplatPickResult[], maxResults: number): GSplatPickResult[] {
    if (!this.isScalarFilterActive) {
      return results
    }
    if (maxResults <= 0) {
      return []
    }

    const filtered: GSplatPickResult[] = []
    for (const result of results) {
      if (!this.passesScalarFilter(result.splatInfo, result.splatIndex)) {
        continue
      }

      filtered.push(result)
      if (filtered.length >= maxResults) {
        break
      }
    }

    return filtered
  }
}
